/**
 * Quick View functionality for product cards
 * Handles drawer opening, content loading, and variant updates using Section Rendering API
 */

import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent, QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import {
  splitName,
  isValidEmail,
  getNotifyMeTranslation,
  setNotifyMeFieldError,
  clearNotifyMeFieldError,
  validateNotifyMeNameField,
  validateNotifyMeEmailField,
  getNotifyMeErrorMessage,
  submitNotifyMeNotification
} from './notify-me.js';
import './quick-view-multi-option.js';

/**
 * @typedef {Object} QuickViewRefs
 * @property {HTMLElement} [contentContainer]
 * @property {HTMLElement} [quickViewContent]
 * @property {HTMLFormElement} [productForm]
 * @property {HTMLElement} [variantSelector]
 * @property {HTMLElement} [quantitySection]
 */

/**
 * Quick View Component - Manages quick view drawer content loading and variant updates
 * @extends {Component<QuickViewRefs>}
 */
class QuickViewComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {boolean} */
  #variantUpdateInProgress = false;
  /** @type {EventListener | null} */
  #cartAddHandler = null;
  /** @type {EventListener | null} */
  #quantityUpdateHandler = null;
  /** @type {EventListener | null} */
  #drawerCloseHandler = null;
  /** @type {string | null} */
  #lastClickedOptionPosition = null;
  /** @type {string | null} */
  #lastClickedOptionValue = null;
  /** @type {number | null} */
  #notifyMeCheckTimeout = null;
  /** @type {boolean} */
  #notifyMeCheckInProgress = false;
  /** @type {boolean} */
  #notifyMeSubmitting = false;
  /** @type {AbortController | null} */
  #notifyMeAbortController = null;
  /** @type {EventListener | null} */
  #notifyMeSubmitHandler = null;

  /** @type {number | null} - Timeout for auto-hiding error message */
  #errorMessageTimeout = null;
  /** @type {MutationObserver | null} - Observer for Shop Pay button changes */
  #shopPayObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#initializeQuickView();
    this.#setupDrawerCloseListener();
    
    // Listen for footer-loaded event to re-check notification status
    this.addEventListener('quick-view-footer-loaded', this.#handleFooterLoaded.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#notifyMeAbortController?.abort();
    this.#shopPayObserver?.disconnect();
    this.#shopPayObserver = null;
    if (this.#cartAddHandler) {
      document.removeEventListener(CartAddEvent.eventName, this.#cartAddHandler);
    }
    if (this.#quantityUpdateHandler) {
      this.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#quantityUpdateHandler);
    }
    if (this.#drawerCloseHandler) {
      const drawer = this.closest('drawer-component');
      if (drawer) {
        drawer.removeEventListener('dialog:close', this.#drawerCloseHandler);
        const dialog = drawer.querySelector('dialog');
        if (dialog) {
          dialog.removeEventListener('close', this.#drawerCloseHandler);
        }
      }
    }
    // Remove notify me submit handler if present
    if (this.#notifyMeSubmitHandler) {
      const submitBtn = this.querySelector('[data-notify-me-submit]');
      if (submitBtn instanceof HTMLElement) {
        submitBtn.removeEventListener('click', this.#notifyMeSubmitHandler);
      }
    }
    // Remove footer-loaded event listener
    this.removeEventListener('quick-view-footer-loaded', this.#handleFooterLoaded);
    // Disconnect wishlist observer
    this.#disconnectWishlistObserver();
  }
  
  /**
   * Handle footer loaded event - re-check notification status
   * @param {CustomEvent} event
   */
  async #handleFooterLoaded(event) {
    const { wasSubscribed, subscribedSku, drawerLoader } = event.detail || {};
    
    // Store restore state if needed
    if (wasSubscribed && subscribedSku) {
      this.dataset.pendingNotifyMeRestore = 'true';
      this.dataset.pendingNotifyMeSku = subscribedSku;
    }
    
    // Re-initialize notify me to check status and restore state
    setTimeout(async () => {
      await this.#initializeNotifyMe();

      // After footer reloads, restore stock visibility state.
      // The footer is always rendered with add-to-cart visible by default from the server.
      // We must re-apply the stock toggle based on what the main content currently shows.
      const quickViewEl = this.querySelector('[data-quick-view-content]') || this.querySelector('.quick-view') || this;
      const drawerItemsComponentEl = this.closest('drawer-items-component');
      const footerSlotEl = drawerItemsComponentEl?.querySelector('[data-drawer-footer]');
      
      // Check for three possible states:
      // 1. truly_unavailable → .quick-view__stock--unavailable exists
      // 2. OOS               → .quick-view__stock--out exists
      // 3. in stock          → neither class exists
      const isTrulyUnavailable = quickViewEl instanceof HTMLElement &&
        !!quickViewEl.querySelector('[data-stock-status] .quick-view__stock--unavailable');
      const isCurrentlyOos = quickViewEl instanceof HTMLElement &&
        !!quickViewEl.querySelector('[data-stock-status] .quick-view__stock--out');
      
      if (isTrulyUnavailable) {
        // Truly unavailable: show purchase wrapper with disabled "Unavailable" button, hide notify-me
        this.#updateStockStatus(quickViewEl, footerSlotEl, false, null, true);
      } else if (isCurrentlyOos) {
        // Genuine OOS: hide purchase wrapper, show notify-me
        this.#updateStockStatus(quickViewEl, footerSlotEl, false, null, false);
      }

      // Hide drawer loader after notification status check completes.
      // Skip hiding if another variant update is already in progress (e.g. the Pass-2
      // restoration call started before this 100ms timeout fired).  The in-progress call's
      // own #handleFooterLoaded will hide the loader when it finishes, preventing the
      // "show → hide → show → hide" flicker that users would otherwise see.
      const drawerItemsComponent = this.closest('drawer-items-component');
      const loader = drawerLoader || drawerItemsComponent?.querySelector('[data-drawer-loader]');
      if (loader instanceof HTMLElement && !this.#variantUpdateInProgress) {
        loader.classList.add('hidden');
        loader.setAttribute('aria-hidden', 'true');
        loader.setAttribute('aria-busy', 'false');
      }
    }, 100);
  }

  #initializeQuickView() {
    // Check for multi-option picker first (takes precedence over standard variant picker)
    const multiOptionPicker = this.querySelector('quick-view-multi-option');
    if (multiOptionPicker instanceof HTMLElement) {
      this.#initializeMultiOptionPicker();
    } else {
      // Initialize standard variant selection if no multi-option picker
      const variantPicker = this.querySelector('quick-view-variant-picker');
      if (!variantPicker) {
        const variantSelector = this.querySelector('[data-variant-selector]');
        if (variantSelector) {
          this.#initializeVariantSelection();
        }
      }
    }
    
    this.#initializeQuantitySelector();
    this.#initializeFormSubmission();
    this.#setupAddToCartCheckmarkAnimation();

    // Initialize notify me form if it exists in the content
    const notifyMeFormWrapper = this.querySelector('[data-notify-me-form-wrapper]');
    if (notifyMeFormWrapper instanceof HTMLElement) {
      this.#initializeNotifyMe();
    }

    const sizeChartBtn = this.querySelector('.quick-view__size-chart');
    if (sizeChartBtn instanceof HTMLElement) {
      this.#initializeSizeChart(sizeChartBtn);
    }
  }

  /**
   * Initialize multi-option picker for products with more than 3 options
   * Listens for selection changes and triggers variant updates
   */
  #initializeMultiOptionPicker() {
    const quickView = this;
    
    if (quickView.dataset.multiOptionInitialized === 'true') {
      return;
    }
    
    quickView.dataset.multiOptionInitialized = 'true';
    
    // Listen for multi-option selection changes
    quickView.addEventListener('quick-view-multi-option-change', async (e) => {
      if (!(e instanceof CustomEvent)) return;
      
      const { optionValueIds, matchingVariant } = e.detail;
      
      const workingContainer = quickView.querySelector('[data-quick-view-content]') ||
                               quickView.querySelector('.quick-view') ||
                               quickView;

      if (!(workingContainer instanceof HTMLElement)) return;

      // CRITICAL: Cancel any pending variant updates to prevent race conditions
      if (this.#abortController) {
        this.#abortController.abort();
        this.#abortController = null;
        this.#variantUpdateInProgress = false;
      }

      // Update variant using the multi-option selected values
      await this.#updateMultiOptionVariant(workingContainer, optionValueIds);
    }, false);
  }

  /**
   * Update variant for multi-option products using Section Rendering API
   * @param {HTMLElement} container
   * @param {string[]} optionValueIds - Array of option value IDs from multi-option picker
   */
  async #updateMultiOptionVariant(container, optionValueIds) {
    if (!(container instanceof HTMLElement) || this.#variantUpdateInProgress) return;
    
    this.#variantUpdateInProgress = true;

    // Show drawer loader when variant selection starts
    this.#showDrawerLoader(container);

    try {
      const quickView = this.#findQuickViewContainer(container);
      if (!quickView) {
        console.error('Quick View: Container not found');
        return;
      }

      const { productId, productHandle, productUrl } = quickView.dataset;
      if (!productId && !productHandle && !productUrl) {
        console.error('Quick View: Missing product identifier');
        return;
      }

      const drawerItemsComponent = quickView.closest('drawer-items-component');
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');

      if (optionValueIds.length === 0) {
        this.#disableAddToCart(container, footerSlot);
        return;
      }

      const url = this.#buildVariantUpdateUrl(productUrl || null, productHandle || null, productId || null, optionValueIds);

      this.#abortController?.abort();
      this.#abortController = new AbortController();

      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
        signal: this.#abortController.signal
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const html = await response.text();
      const updatedContent = this.#parseUpdatedContent(html);
      if (!updatedContent) return;

      // Update container data attributes
      if (updatedContent instanceof HTMLElement) {
        quickView.dataset.productId = updatedContent.dataset.productId || productId || '';
        quickView.dataset.productHandle = updatedContent.dataset.productHandle || productHandle || '';
        quickView.dataset.productUrl = updatedContent.dataset.productUrl || productUrl || '';
        if (updatedContent.dataset.selectedVariant) {
          quickView.dataset.selectedVariant = updatedContent.dataset.selectedVariant;
        }
      }

      // Determine variant info from the server-rendered response
      const resolvedVariantId = updatedContent instanceof HTMLElement
        ? (updatedContent.dataset.selectedVariant || null)
        : null;

      // Determine availability from server-rendered stock status
      const updatedStockEl = updatedContent.querySelector('[data-stock-status]');
      const isTrulyUnavailable = !!updatedStockEl?.querySelector('.quick-view__stock--unavailable');
      const isInStock = !isTrulyUnavailable && (updatedStockEl
        ? !updatedStockEl.querySelector('.quick-view__stock--out')
        : true);

      // Build a minimal variant-like object from server response
      const resolvedVariant = resolvedVariantId ? {
        id: resolvedVariantId,
        available: isInStock,
        sku: updatedContent.querySelector('[data-sku]')?.getAttribute('data-sku-value') || '',
        title: updatedContent.querySelector('.quick-view__title')?.textContent?.trim() || '',
      } : null;

      if (resolvedVariant) {
        this.dispatchEvent(new CustomEvent('variant:update', {
          bubbles: true,
          detail: {
            variantId: resolvedVariant.id,
            sku: resolvedVariant.sku,
            available: resolvedVariant.available,
            title: resolvedVariant.title
          }
        }));
      }

      // Update stock status UI in both main content and footer
      // Skip notification check here - we'll do it after morphing on the new form element
      this.#updateStockStatus(quickView, footerSlot, isInStock, resolvedVariant, isTrulyUnavailable, true);

      // Morph only non-multi-option elements (preserve the multi-option picker state)
      this.#morphMultiOptionElements(quickView, updatedContent);

      // Update variant IDs in all forms
      if (resolvedVariantId) {
        this.#updateVariantIds(quickView, footerSlot, resolvedVariantId);

        // Update quantity selector
        const quantitySelector = quickView.querySelector('quantity-selector-component');
        if (quantitySelector instanceof HTMLElement) {
          quantitySelector.setAttribute('data-variant-id', resolvedVariantId);
        }
      }

      removeDuplicates(quickView);

      // Reload footer with new variant
      if (drawerItemsComponent instanceof HTMLElement) {
        const { productHandle: updatedHandle, productUrl: updatedUrl } = quickView.dataset;
        await loadQuickViewFooter(drawerItemsComponent, updatedHandle || null, updatedUrl || null, resolvedVariantId, isInStock);
      }

      requestAnimationFrame(async () => {
        removeDuplicates(quickView);
        const productForm = quickView.querySelector('form[data-type="add-to-cart-form"]');
        if (productForm instanceof HTMLFormElement) {
          delete productForm.dataset.formSubmissionPrevented;
        }
        // Don't reinitialize multi-option picker to preserve state
        this.#initializeQuantitySelector();
        this.#initializeFormSubmission();
        this.#setupAddToCartCheckmarkAnimation();
        
        // Check notification status AFTER morphing (form element is now updated)
        // This ensures the form UI state (thank you message) is properly restored
        const notifyMeForm = quickView.querySelector('quick-view-notify-me-form');
        if (notifyMeForm instanceof HTMLElement && resolvedVariant && !isInStock) {
          await this.#checkVariantNotificationAfterUpdate(notifyMeForm, footerSlot, resolvedVariant.sku);
        }
        
        // Initialize notify me form handlers (but don't check status again - already checked above)
        const notifyMeFormWrapper = this.querySelector('[data-notify-me-form-wrapper]');
        if (notifyMeFormWrapper instanceof HTMLElement) {
          this.#initializeNotifyMe();
        }
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Quick View: Error updating multi-option variant', error);
      }
    } finally {
      this.#variantUpdateInProgress = false;
    }
  }

  /**
   * Update quantity selector with new variant data
   * Shared method used by both standard and multi-option variant updates
   * @param {HTMLElement} container - The container with quantity selector
   * @param {HTMLElement} updatedContent - The updated content with new quantity data
   */
  #updateQuantitySelector(container, updatedContent) {
    const currentQty = container.querySelector('quantity-selector-component');
    const updatedQty = updatedContent.querySelector('quantity-selector-component');
    
    if (!currentQty || !updatedQty) return;

    const newVariantId = updatedQty.getAttribute('data-variant-id');
    if (newVariantId) currentQty.setAttribute('data-variant-id', newVariantId);

    const updatedInput = updatedQty.querySelector('input[type="number"]');
    const currentInput = currentQty.querySelector('input[type="number"]');
    
    if (currentInput instanceof HTMLInputElement && updatedInput instanceof HTMLInputElement) {
      const newMax = updatedInput.getAttribute('max') || '999';
      const newMin = updatedInput.getAttribute('min') || '1';
      const newStep = updatedInput.getAttribute('step') || '1';
      
      // Update inventory data attributes if they exist
      const inventoryQuantity = updatedInput.getAttribute('data-inventory-quantity');
      const inventoryPolicy = updatedInput.getAttribute('data-inventory-policy');
      if (inventoryQuantity !== null) {
        currentInput.setAttribute('data-inventory-quantity', inventoryQuantity);
      } else {
        currentInput.removeAttribute('data-inventory-quantity');
      }
      if (inventoryPolicy !== null) {
        currentInput.setAttribute('data-inventory-policy', inventoryPolicy);
      } else {
        currentInput.removeAttribute('data-inventory-policy');
      }

      if ('updateConstraints' in currentQty && typeof currentQty.updateConstraints === 'function') {
        currentQty.updateConstraints(newMin, newMax === '999' ? null : newMax, newStep);
      } else {
        currentInput.setAttribute('max', newMax);
        currentInput.setAttribute('min', newMin);
        currentInput.setAttribute('step', newStep);
      }
      
      // Update button states after updating constraints and inventory data
      if ('updateButtonStates' in currentQty && typeof currentQty.updateButtonStates === 'function') {
        currentQty.updateButtonStates();
      }
    }

    const currentWrapper = currentQty.closest('.quantity-selector-wrapper');
    const updatedWrapper = updatedQty.closest('.quantity-selector-wrapper');
    if (currentWrapper && updatedWrapper) {
      morph(currentWrapper, updatedWrapper);
    }

    // Sync quantity display span (e.g. "3") so it matches the new variant's min after reset
    const updatedQuantityValueEl = updatedContent.querySelector('[data-quantity-value]');
    const currentQuantityValueEl = container.querySelector('[data-quantity-value]');
    if (updatedQuantityValueEl && currentQuantityValueEl) {
      currentQuantityValueEl.textContent = updatedQuantityValueEl.textContent.trim();
    }
  }

  /**
   * Morph elements for multi-option variant updates
   * Preserves the multi-option picker component state
   * @param {HTMLElement} quickView
   * @param {HTMLElement} updatedContent
   */
  #morphMultiOptionElements(quickView, updatedContent) {
    // Morph product image
    const currentImage = quickView.querySelector('[data-quick-view-image]');
    const updatedImage = updatedContent.querySelector('[data-quick-view-image]');
    if (currentImage && updatedImage) {
      morph(currentImage, updatedImage);
    }

    // Morph SKU
    const currentSku = quickView.querySelector('[data-sku]');
    const updatedSku = updatedContent.querySelector('[data-sku]');
    if (currentSku && updatedSku) {
      morph(currentSku, updatedSku);
    }

    const currentTitle = quickView.querySelector('.quick-view__title');
    const updatedTitle = updatedContent.querySelector('.quick-view__title');
    if (currentTitle instanceof HTMLElement && updatedTitle instanceof HTMLElement) {
      morph(currentTitle, updatedTitle);
    }

    // Morph pricing
    const currentPricing = quickView.querySelector('[data-pricing]');
    const updatedPricing = updatedContent.querySelector('[data-pricing]');
    if (currentPricing && updatedPricing) {
      morph(currentPricing, updatedPricing);
    }

    // Morph stock status
    const currentStock = quickView.querySelector('[data-stock-status]');
    const updatedStock = updatedContent.querySelector('[data-stock-status]');
    if (currentStock && updatedStock) {
      morph(currentStock, updatedStock);
    }

    // Morph notify me form wrapper
    const currentNotifyMe = quickView.querySelector('[data-notify-me-form-wrapper]');
    const updatedNotifyMe = updatedContent.querySelector('[data-notify-me-form-wrapper]');
    if (currentNotifyMe && updatedNotifyMe) {
      morph(currentNotifyMe, updatedNotifyMe);
    }

    // Morph shipping badge
    const currentShipping = quickView.querySelector('[data-shipping-badge]');
    const updatedShipping = updatedContent.querySelector('[data-shipping-badge]');
    if (currentShipping && updatedShipping) {
      morph(currentShipping, updatedShipping);
    }
    
    // Morph budget pay
    const currentBudgetPay = quickView.querySelector('[data-budget-pay]');
    const updatedBudgetPay = updatedContent.querySelector('[data-budget-pay]');
    if (currentBudgetPay && updatedBudgetPay) {
      morph(currentBudgetPay, updatedBudgetPay);
    }

    // Update quantity selector using shared method
    this.#updateQuantitySelector(quickView, updatedContent);
  }

  #initializeVariantSelection() {
    const quickView = this;
    const optionInputs = quickView.querySelectorAll('[data-option-input]');

    if (optionInputs.length === 0 || quickView.dataset.variantSelectionInitialized === 'true') {
      return;
    }

    quickView.dataset.variantSelectionInitialized = 'true';

    quickView.addEventListener('change', async (e) => {
      if (!(e.target instanceof HTMLInputElement)) return;
      const input = e.target;

      if (!input.hasAttribute('data-option-input') || !input.checked) return;

      const workingContainer = input.closest('[data-quick-view-content]') ||
                               input.closest('.quick-view') ||
                               quickView;

      if (!(workingContainer instanceof HTMLElement)) return;

      // CRITICAL: Cancel any pending variant updates to prevent race conditions
      // when swatches are clicked rapidly
      if (this.#abortController) {
        this.#abortController.abort();
        this.#abortController = null;
        // Reset the flag since we're cancelling the previous operation
        // The finally block will also reset it, but this ensures immediate reset
        this.#variantUpdateInProgress = false;
      }

      // Track which option was clicked for state restoration after morph
      this.#lastClickedOptionPosition = input.dataset.optionPosition || null;
      this.#lastClickedOptionValue = input.value;

      // Update swatch selected state immediately and synchronously
      // This must happen before any async operations
      this.#updateSwatchSelectedState(input, workingContainer);

      // Then update variant asynchronously
      await this.#updateVariant(workingContainer);
    }, false);
  }

  /**
   * Update variant using Section Rendering API
   * @param {HTMLElement} container
   */
  async #updateVariant(container) {
    if (!(container instanceof HTMLElement) || this.#variantUpdateInProgress) return;

    this.#variantUpdateInProgress = true;

    // Show drawer loader when variant selection starts
    this.#showDrawerLoader(container);

    try {
      const quickView = this.#findQuickViewContainer(container);
      if (!quickView) {
        console.error('Quick View: Container not found');
        return;
      }

      const { productId, productHandle, productUrl } = quickView.dataset;
      if (!productId && !productHandle && !productUrl) {
        console.error('Quick View: Missing product identifier');
        return;
      }

      // Pass all selected option IDs to the server.
      // Since we're showing all swatches (including unavailable ones), the server
      // will correctly resolve the variant based on all selected options.
      const optionValueIds = this.#getSelectedOptionValueIds(quickView);

      const drawerItemsComponent = quickView.closest('drawer-items-component');
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');

      if (optionValueIds.length === 0) {
        this.#disableAddToCart(container, footerSlot);
        return;
      }

      const url = this.#buildVariantUpdateUrl(productUrl || null, productHandle || null, productId || null, optionValueIds);

      this.#abortController?.abort();
      this.#abortController = new AbortController();

      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        cache: 'no-store',
        signal: this.#abortController.signal
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const html = await response.text();
      const updatedContent = this.#parseUpdatedContent(html);
      if (!updatedContent) return;

      // Update container data attributes
      if (updatedContent instanceof HTMLElement) {
        quickView.dataset.productId = updatedContent.dataset.productId || productId || '';
        quickView.dataset.productHandle = updatedContent.dataset.productHandle || productHandle || '';
        quickView.dataset.productUrl = updatedContent.dataset.productUrl || productUrl || '';
        // Keep data-selected-variant in sync so that reopening with cached content
        // can pick up the correct variant ID (fixes Add to Cart passing wrong variant).
        if (updatedContent.dataset.selectedVariant) {
          quickView.dataset.selectedVariant = updatedContent.dataset.selectedVariant;
        }
      }

      // Determine variant info from the server-rendered response.
      // The server resolves the correct variant via option_values parameter.
      // We read the server-resolved variant ID from data-selected-variant on the .quick-view root.
      const resolvedVariantId = updatedContent instanceof HTMLElement
        ? (updatedContent.dataset.selectedVariant || null)
        : null;

      // Determine availability from server-rendered stock status.
      // Three states are possible:
      //   1. In stock     → no --out or --unavailable class
      //   2. OOS          → .quick-view__stock--out  (variant exists but sold out → show Notify Me)
      //   3. Unavailable  → .quick-view__stock--unavailable (no variant for this combo → show disabled button)
      const updatedStockEl = updatedContent.querySelector('[data-stock-status]');
      const isTrulyUnavailable = !!updatedStockEl?.querySelector('.quick-view__stock--unavailable');
      const isInStock = !isTrulyUnavailable && (updatedStockEl
        ? !updatedStockEl.querySelector('.quick-view__stock--out')
        : true);

      // Build a minimal variant-like object from server response for compatibility
      const resolvedVariant = resolvedVariantId ? {
        id: resolvedVariantId,
        available: isInStock,
        sku: updatedContent.querySelector('[data-sku]')?.getAttribute('data-sku-value') || '',
        title: updatedContent.querySelector('.quick-view__title')?.textContent?.trim() || '',
      } : null;

      if (resolvedVariant) {
        this.dispatchEvent(new CustomEvent('variant:update', {
          bubbles: true,
          detail: {
            variantId: resolvedVariant.id,
            sku: resolvedVariant.sku,
            available: resolvedVariant.available,
            title: resolvedVariant.title
          }
        }));
      }
      
      // Update stock status UI in both main content and footer
      this.#updateStockStatus(quickView, footerSlot, isInStock, resolvedVariant, isTrulyUnavailable);

      // Morph form and elements
      const currentForm = quickView.querySelector('product-form-component');
      const updatedForm = updatedContent.querySelector('product-form-component');
      if (currentForm && updatedForm) {
        morph(currentForm, updatedForm);
      }

      if (updatedContent instanceof HTMLElement) {
        this.#morphQuickViewElements(quickView, updatedContent);
      }

      // Update variant IDs in all forms (use server-resolved variant ID)
      if (resolvedVariantId) {
        this.#updateVariantIds(quickView, footerSlot, resolvedVariantId);

        // Update quantity selector
        const quantitySelector = quickView.querySelector('quantity-selector-component');
        if (quantitySelector instanceof HTMLElement) {
          quantitySelector.setAttribute('data-variant-id', resolvedVariantId);
        }
      }

      removeDuplicates(quickView);

      // Reload footer with new variant - loader will be hidden after footer loads
      if (drawerItemsComponent instanceof HTMLElement) {
        const { productHandle: updatedHandle, productUrl: updatedUrl } = quickView.dataset;
        await loadQuickViewFooter(drawerItemsComponent, updatedHandle || null, updatedUrl || null, resolvedVariantId, isInStock);
      }

      requestAnimationFrame(() => {
        removeDuplicates(quickView);
        // NOTE: Do NOT delete variantSelectionInitialized here.
        // Deleting it would cause #initializeVariantSelection to add a new change listener
        // on top of the existing one each time a variant update completes, resulting in
        // stacked duplicate listeners that fire multiple Section Rendering API calls per click.
        const productForm = quickView.querySelector('form[data-type="add-to-cart-form"]');
        if (productForm instanceof HTMLFormElement) {
          delete productForm.dataset.formSubmissionPrevented;
        }
        this.#initializeQuickView();
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Quick View: Error updating variant', error);
      }
    } finally {
      this.#variantUpdateInProgress = false;
      // Note: Loader will be hidden after footer loads (see loadQuickViewFooter)
    }
  }

  /**
   * Find quick view container from element
   * @param {HTMLElement} container
   * @returns {HTMLElement | null}
   */
  #findQuickViewContainer(container) {
    return container.querySelector('[data-quick-view-content]') ||
           container.querySelector('.quick-view') ||
           (container.classList.contains('quick-view') ? container : null) ||
           container.closest('[data-quick-view-content]') ||
           container.closest('.quick-view') ||
           container;
  }

  /**
   * Get selected option_value IDs from checked inputs.
   * Works for products with any number of variants (including >250).
   * @param {HTMLElement} quickView
   * @returns {string[]}
   */
  #getSelectedOptionValueIds(quickView) {
    /** @type {string[]} */
    const ids = [];
    quickView.querySelectorAll('[data-option-input]:checked').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const id = input.dataset.optionValueId;
      if (id) ids.push(id);
    });
    return ids;
  }

  /**
   * Disable add to cart buttons
   * @param {HTMLElement} container
   * @param {Element | null | undefined} footerSlot
   */
  #disableAddToCart(container, footerSlot) {
    const addToCartBtn = container.querySelector('button[type="submit"][name="add"]');
    if (addToCartBtn instanceof HTMLButtonElement) {
      addToCartBtn.disabled = true;
    }
    if (footerSlot) {
      const footerBtn = footerSlot.querySelector('button[type="submit"][name="add"]');
      if (footerBtn instanceof HTMLButtonElement) {
        footerBtn.disabled = true;
      }
    }
  }

  /**
   * Update stock status UI
   * @param {HTMLElement} quickView
   * @param {Element | null | undefined} footerSlot
   * @param {boolean} isInStock
   * @param {any} matchingVariant
   * @param {boolean} [isTrulyUnavailable] - True when no variant exists for this option combo.
   *   In this case show a disabled "Unavailable" button (not Notify Me).
   * @param {boolean} [skipNotificationCheck] - Skip notification check (for multi-option updates where check happens after morphing)
   */
  #updateStockStatus(quickView, footerSlot, isInStock, matchingVariant, isTrulyUnavailable = false, skipNotificationCheck = false) {
    // Cache translation strings (only fetch once per call)
    const theme = /** @type {any} */ (window).theme;
    const addToCartText = theme?.strings?.addToCart || 'Add To Cart';
    const unavailableText = 'Unavailable';

    // --- Three-state display logic ---
    // In stock    : purchase buttons visible, notify-me hidden
    // OOS         : purchase buttons hidden, notify-me shown
    // Unavailable : purchase buttons visible (disabled "Unavailable"), notify-me hidden

    // Update main content visibility
    const purchaseWrapper = quickView.querySelector('[data-purchase-buttons]');
    const notifyMeWrapper = quickView.querySelector('.notify_me_wrapper');
    if (purchaseWrapper instanceof HTMLElement) {
      // Show purchase wrapper when in-stock OR when truly unavailable (for the disabled button)
      purchaseWrapper.style.display = (isInStock || isTrulyUnavailable) ? '' : 'none';
    }
    if (notifyMeWrapper instanceof HTMLElement) {
      // Only show notify-me for genuine OOS (variant exists but sold out)
      notifyMeWrapper.style.display = (!isInStock && !isTrulyUnavailable) ? '' : 'none';
    }

    // Also check footer slot's notify me wrapper
    const footerNotifyMeWrapper = footerSlot?.querySelector('.notify_me_wrapper');
    if (footerNotifyMeWrapper instanceof HTMLElement) {
      footerNotifyMeWrapper.style.display = (!isInStock && !isTrulyUnavailable) ? 'none' : 'none';

      // Add drawer-notify-msg class only for genuine OOS (not truly unavailable)
      this.#setDrawerNotifiedClass(!isInStock && !isTrulyUnavailable);
    } else {
      this.#setDrawerNotifiedClass(false);
    }

    // Update notify me form wrapper in content area
    const contentNotifyMeWrapper = quickView.querySelector('[data-notify-me-form-wrapper]');
    if (contentNotifyMeWrapper instanceof HTMLElement) {
      contentNotifyMeWrapper.style.display = (!isInStock && !isTrulyUnavailable) ? '' : 'none';
    }

      // Update notify me form with new variant data
      const notifyMeForm = quickView.querySelector('quick-view-notify-me-form');
      if (notifyMeForm instanceof HTMLElement && matchingVariant) {
        const skuInput = notifyMeForm.querySelector('[data-notify-me-sku]');
        const variantIdInput = notifyMeForm.querySelector('[data-notify-me-variant-id]');
        
        if (skuInput instanceof HTMLInputElement) {
          skuInput.value = matchingVariant.sku || '';
        }
        if (variantIdInput instanceof HTMLInputElement) {
          variantIdInput.value = matchingVariant.id?.toString() || '';
        }
        
        // Update form data attributes
        notifyMeForm.dataset.variantId = matchingVariant.id?.toString() || '';
        notifyMeForm.dataset.variantSku = matchingVariant.sku || '';
        
        // Preserve customer email if it exists (in case form was morphed)
        const customerId = notifyMeForm.dataset.customerId;
        if (customerId) {
          const emailInput = notifyMeForm.querySelector('[data-notify-me-email]');
          const nameInput = notifyMeForm.querySelector('[data-notify-me-name]');
          
          // If email input exists but is empty, it might have been reset during morph
          // The server should provide it, but we'll preserve it if it was there before
          if (emailInput instanceof HTMLInputElement && !emailInput.value && emailInput.hasAttribute('readonly')) {
            // Email should be in the HTML from server, but if it's missing, we can't set it client-side
            // The readonly attribute indicates it should have been pre-filled
          }
        }
        
        // Reset subscribed state when variant changes (unless it's already notified)
        // Check will be done in #checkVariantNotificationAfterUpdate
        // Skip if flag is set (for multi-option updates, we check after morphing)
        if (!isInStock && !skipNotificationCheck && matchingVariant?.sku) {
          this.#checkVariantNotificationAfterUpdate(notifyMeForm, footerSlot, matchingVariant.sku);
        }
        
        // Update button state after variant change (form may have been morphed)
        const footerNotifyBtn = footerSlot?.querySelector('[data-notify-me]');
        if (footerNotifyBtn) {
          this.#updateNotifyMeButtonState(notifyMeForm, footerNotifyBtn);
        }
    }

    // Update main add to cart button
    // - In stock     : enabled, "Add To Cart" text
    // - OOS          : hidden (purchase wrapper hidden), so no update needed
    // - Unavailable  : disabled, "Unavailable" text
    const mainAddBtn = quickView.querySelector('button[type="submit"][name="add"]');
    if (mainAddBtn instanceof HTMLButtonElement) {
      mainAddBtn.disabled = !isInStock; // disabled when OOS or unavailable
      const mainTextEl = mainAddBtn.querySelector('[data-add-text]');
      if (mainTextEl instanceof HTMLElement) {
        if (isTrulyUnavailable) {
          mainTextEl.textContent = unavailableText;
        } else if (isInStock) {
          mainTextEl.textContent = addToCartText;
        }
      }
    }

    // Update Shop Pay button state in main content
    this.#disableShopPayButton(quickView, !isInStock);

    // Update footer
    if (footerSlot) {
      const footerPurchaseWrapper = footerSlot.querySelector('[data-purchase-buttons]');
      const footerNotifyMeWrapper = footerSlot.querySelector('.notify_me_wrapper');
      const footerNotifyBtn = footerSlot.querySelector('[data-notify-me]');

      if (footerPurchaseWrapper instanceof HTMLElement) {
        // Show purchase wrapper when in-stock OR truly unavailable (for the disabled button)
        footerPurchaseWrapper.style.display = (isInStock || isTrulyUnavailable) ? '' : 'none';
      }
      if (footerNotifyMeWrapper instanceof HTMLElement) {
        // Only show notify-me for genuine OOS
        footerNotifyMeWrapper.style.display = (!isInStock && !isTrulyUnavailable) ? '' : 'none';
      }
      // Update aria-label when showing notify button (genuine OOS only)
      if (!isInStock && !isTrulyUnavailable && footerNotifyBtn instanceof HTMLButtonElement) {
        const variantTitle = matchingVariant?.title || '';
        const productHandle = quickView.dataset.productHandle || '';
        const ariaLabel = `Notify me when available: ${productHandle} ${variantTitle}`.trim();
        if (footerNotifyBtn.getAttribute('aria-label') !== ariaLabel) {
          footerNotifyBtn.setAttribute('aria-label', ariaLabel);
        }
      }

      // Update footer add to cart button
      const footerAddBtn = footerSlot.querySelector('button[type="submit"][name="add"]');
      if (footerAddBtn instanceof HTMLButtonElement) {
        footerAddBtn.disabled = !isInStock; // disabled for OOS and unavailable
        const footerTextEl = footerAddBtn.querySelector('[data-add-text]');
        if (footerTextEl instanceof HTMLElement) {
          if (isTrulyUnavailable) {
            footerTextEl.textContent = unavailableText;
          } else if (isInStock) {
            footerTextEl.textContent = addToCartText;
          }
        }
      }

      // Update Shop Pay button state
      // Shopify's payment button is dynamically rendered, so we need aggressive disabling
      this.#disableShopPayButton(footerSlot, !isInStock);
    }

    this.#syncWishlistProductIconDisabled(quickView, footerSlot, isTrulyUnavailable);
  }

  /** @type {MutationObserver | null} */
  #wishlistObserver = null;

  /**
   * Disable wishlist UI when no variant exists for the selected option combination
   * (same state as disabled "Unavailable" add-to-cart / Shop Pay).
   * @param {HTMLElement | null | undefined} quickView
   * @param {Element | null | undefined} footerSlot
   * @param {boolean} isTrulyUnavailable
   */
  #syncWishlistProductIconDisabled(quickView, footerSlot, isTrulyUnavailable) {
    const shouldDisable = !!isTrulyUnavailable;
    /** @type {HTMLElement[]} */
    const roots = [];
    if (quickView instanceof HTMLElement) roots.push(quickView);
    if (footerSlot instanceof HTMLElement) roots.push(footerSlot);

    for (const root of roots) {
      this.#disableWishlistElements(root, shouldDisable);
    }

    // Set up MutationObserver to catch dynamically injected wishlist elements (from third-party apps)
    if (shouldDisable) {
      this.#setupWishlistObserver(roots);
    } else {
      this.#disconnectWishlistObserver();
    }
  }

  /**
   * Disable wishlist elements within a container
   * @param {HTMLElement} root
   * @param {boolean} shouldDisable
   */
  #disableWishlistElements(root, shouldDisable) {
    root.querySelectorAll('wishlist-product-icon').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      if (shouldDisable) {
        el.setAttribute('aria-disabled', 'true');
        el.style.pointerEvents = 'none';
        el.style.opacity = '0.5';
      } else {
        el.removeAttribute('aria-disabled');
        el.style.pointerEvents = '';
        el.style.opacity = '';
      }
      el.querySelectorAll('.wishlist-product-icon__button, button').forEach((btn) => {
        if (btn instanceof HTMLButtonElement) {
          btn.disabled = shouldDisable;
          if (shouldDisable) {
            btn.style.pointerEvents = 'none';
          } else {
            btn.style.pointerEvents = '';
          }
        }
      });
    });
  }

  /**
   * Set up MutationObserver to watch for dynamically added wishlist elements
   * @param {HTMLElement[]} roots
   */
  #setupWishlistObserver(roots) {
    this.#disconnectWishlistObserver();

    this.#wishlistObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          
          // Check if added node is a wishlist element or contains one
          if (node.tagName === 'WISHLIST-PRODUCT-ICON') {
            this.#disableWishlistElements(node.parentElement || node, true);
          } else {
            const wishlistIcons = node.querySelectorAll('wishlist-product-icon');
            wishlistIcons.forEach((icon) => {
              if (icon instanceof HTMLElement) {
                this.#disableWishlistElements(icon.parentElement || icon, true);
              }
            });
          }
        }
      }
    });

    for (const root of roots) {
      this.#wishlistObserver.observe(root, {
        childList: true,
        subtree: true,
      });
    }
  }

  /**
   * Disconnect wishlist MutationObserver
   */
  #disconnectWishlistObserver() {
    if (this.#wishlistObserver) {
      this.#wishlistObserver.disconnect();
      this.#wishlistObserver = null;
    }
  }

  /**
   * Aggressively disable Shop Pay buttons in a container
   * Handles dynamically rendered buttons and re-enables after mutations
   * @param {HTMLElement | null | undefined} container - Container to search for Shop Pay buttons
   * @param {boolean} shouldDisable - Whether to disable (true) or enable (false) the button
   */
  #disableShopPayButton(container, shouldDisable) {
    if (!container) return;

    // Find all Shop Pay forms
    const shopPayForms = container.querySelectorAll('[id^="BuyShopPayForm-"]');
    
    shopPayForms.forEach((form) => {
      if (!(form instanceof HTMLElement)) return;

      // Disable form submission
      if (shouldDisable) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }, { capture: true, once: false });
      }

      // Find wrapper and disable via CSS
      const wrapper = form.querySelector('[class*="shoppay"], .quick-view__shop-pay');
      if (wrapper instanceof HTMLElement) {
        if (shouldDisable) {
          wrapper.style.pointerEvents = 'none';
          wrapper.style.opacity = '0.5';
          wrapper.style.cursor = 'not-allowed';
          wrapper.setAttribute('aria-disabled', 'true');
        } else {
          wrapper.style.pointerEvents = '';
          wrapper.style.opacity = '';
          wrapper.style.cursor = '';
          wrapper.removeAttribute('aria-disabled');
        }
      }

      // Find and disable all possible button selectors
      const buttonSelectors = [
        'button',
        '[role="button"]',
        '.shopify-payment-button__button',
        'shopify-accelerated-checkout',
        '[class*="shop-pay"]',
        '[class*="shoppay"]'
      ];

      const disableButton = (/** @type {HTMLElement} */ btn) => {
        if (btn instanceof HTMLButtonElement) {
          btn.disabled = shouldDisable;
        } else if (btn instanceof HTMLElement) {
          if (shouldDisable) {
            btn.style.pointerEvents = 'none';
            btn.style.opacity = '0.5';
            btn.style.cursor = 'not-allowed';
            btn.setAttribute('aria-disabled', 'true');
            // Prevent click events
            btn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              return false;
            }, { capture: true, once: false });
          } else {
            btn.style.pointerEvents = '';
            btn.style.opacity = '';
            btn.style.cursor = '';
            btn.removeAttribute('aria-disabled');
          }
        }
      };

      // Disable all buttons found immediately
      buttonSelectors.forEach((selector) => {
        const buttons = form.querySelectorAll(selector);
        buttons.forEach(disableButton);
      });

      // Also search in wrapper if it exists
      if (wrapper) {
        buttonSelectors.forEach((selector) => {
          const buttons = wrapper.querySelectorAll(selector);
          buttons.forEach(disableButton);
        });
      }

      // Use MutationObserver to catch dynamically added buttons
      if (shouldDisable) {
        // Disconnect existing observer if any
        if (this.#shopPayObserver) {
          this.#shopPayObserver.disconnect();
        }
        
        this.#shopPayObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof HTMLElement) {
                // Check if the added node is a Shop Pay button
                buttonSelectors.forEach((selector) => {
                  if (node.matches && node.matches(selector)) {
                    disableButton(node);
                  }
                  // Also check children
                  const buttons = node.querySelectorAll?.(selector);
                  if (buttons) {
                    buttons.forEach(disableButton);
                  }
                });
              }
            });
          });
        });

        this.#shopPayObserver.observe(form, {
          childList: true,
          subtree: true,
          attributes: false
        });
      } else if (this.#shopPayObserver) {
        // Re-enable: disconnect observer
        this.#shopPayObserver.disconnect();
        this.#shopPayObserver = null;
      }

      // Also disable with a delay to catch async rendering
      if (shouldDisable) {
        setTimeout(() => {
          buttonSelectors.forEach((selector) => {
            const buttons = form.querySelectorAll(selector);
            buttons.forEach(disableButton);
          });
          if (wrapper) {
            buttonSelectors.forEach((selector) => {
              const buttons = wrapper.querySelectorAll(selector);
              buttons.forEach(disableButton);
            });
          }
        }, 100);

        // Second delay for slower async rendering
        setTimeout(() => {
          buttonSelectors.forEach((selector) => {
            const buttons = form.querySelectorAll(selector);
            buttons.forEach(disableButton);
          });
          if (wrapper) {
            buttonSelectors.forEach((selector) => {
              const buttons = wrapper.querySelectorAll(selector);
              buttons.forEach(disableButton);
            });
          }
        }, 500);
      }
    });
  }

  /**
   * Check if the new variant is already notified after variant update
   * @param {HTMLElement} formElement
   * @param {Element | null | undefined} footerSlot
   * @param {string} sku
   */
  async #checkVariantNotificationAfterUpdate(formElement, footerSlot, sku) {
    const footerNotifyBtn = footerSlot?.querySelector('[data-notify-me]');
    const customerId = formElement.dataset.customerId;
    const apiBaseUrl = formElement.dataset.apiBaseUrl || '/apps/notify-me/v1';

    // Reset to non-subscribed state first
    this.#setNotifyMeSubscribed(formElement, footerNotifyBtn, false);

    // Reset initialization flags to allow re-initialization after morphing
    formElement.dataset.notifyMeInitialized = 'false';
    formElement.dataset.notifyMeChecked = 'false';

    // If logged in, check if this variant is already notified
    if (customerId && sku) {
      const quickView = this.#findQuickViewContainer(this);
      if (!(quickView instanceof HTMLElement)) {
        return;
      }
      
      // Check notification status for selected variant only
      const isNotified = await this.#checkSingleVariantNotificationStatus(sku, quickView, apiBaseUrl);
      
      if (isNotified) {
        this.#setNotifyMeSubscribed(formElement, footerNotifyBtn, true);
        // Update bell icons for this SKU
        this.#updateBellIconsForSku(sku, true, quickView);
      } else {
        // Update bell icons to remove is-notified class
        this.#updateBellIconsForSku(sku, false, quickView);
      }
      
      // Mark as checked to prevent duplicate checks in #initializeNotifyMeFormComponent
      formElement.dataset.notifyMeChecked = 'true';
    } else {
      // No customer or SKU - mark as checked anyway to prevent unnecessary checks
      formElement.dataset.notifyMeChecked = 'true';
    }
  }

  /**
   * Update variant IDs in forms
   * @param {HTMLElement} quickView
   * @param {Element | null | undefined} footerSlot
   * @param {string | number} variantId
   */
  #updateVariantIds(quickView, footerSlot, variantId) {
    const variantIdStr = variantId.toString();

    // Update main content inputs
    quickView.querySelectorAll('input[name="id"], [ref="variantId"]').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.value = variantIdStr;
      }
    });

    // Update product-form-component
    const productFormComponent = quickView.querySelector('product-form-component');
    if (productFormComponent && 'updateVariant' in productFormComponent && typeof productFormComponent.updateVariant === 'function') {
      productFormComponent.updateVariant(variantId);
    }

    // Update footer inputs
    if (footerSlot) {
      footerSlot.querySelectorAll('input[name="id"], [ref="variantId"]').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = variantIdStr;
        }
      });

      const footerForm = footerSlot.querySelector('product-form-component');
      if (footerForm && 'updateVariant' in footerForm && typeof footerForm.updateVariant === 'function') {
        footerForm.updateVariant(variantId);
      }

      const footerContainer = footerSlot.querySelector('[data-quick-view-footer]');
      if (footerContainer instanceof HTMLElement) {
        footerContainer.dataset.variantId = variantIdStr;
      }
    }
  }

  /**
   * Parse updated content from HTML response
   * @param {string} html
   * @returns {Element | null}
   */
  #parseUpdatedContent(html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const section = doc.querySelector('[data-section-id="section-rendering-quick-view"]') ||
                   doc.querySelector('section[data-section-id="section-rendering-quick-view"]') ||
                   doc.querySelector('.quick-view-rendering-section') ||
                   doc.querySelector('section') ||
                   doc.body;

    if (!section) {
      console.error('Quick View: Section not found in response');
      return null;
    }

    const updatedContent = section.querySelector('.quick-view') ||
                          section.querySelector('[data-quick-view-content]') ||
                          section.firstElementChild;

    if (!updatedContent) {
      console.error('Quick View: Updated content not found');
      return null;
    }

    return updatedContent;
  }

  /**
   * Build URL for variant updates using option_value IDs.
   * Uses the option_values parameter supported by Section Rendering API,
   * which lets Shopify resolve the variant server-side (supports >250 variants).
   * @param {string | null} productUrl
   * @param {string | null} productHandle
   * @param {string | number | null} productId
   * @param {string[]} optionValueIds
   * @returns {string}
   */
  #buildVariantUpdateUrl(productUrl, productHandle, productId, optionValueIds) {
    const baseUrl = productUrl || (productHandle ? `/products/${productHandle}` : `/products/${productId}`);
    try {
      const urlObj = new URL(baseUrl, window.location.origin);
      urlObj.search = '';
      urlObj.searchParams.set('section_id', 'section-rendering-quick-view');
      if (optionValueIds.length > 0) {
        urlObj.searchParams.set('option_values', optionValueIds.join(','));
      }
      urlObj.searchParams.set('_', Date.now().toString());
      return urlObj.pathname + urlObj.search;
    } catch {
      const cleanPath = baseUrl.split('?')[0] || baseUrl;
      let url = `${cleanPath}?section_id=section-rendering-quick-view`;
      if (optionValueIds.length > 0) {
        url += `&option_values=${optionValueIds.join(',')}`;
      }
      return url + `&_=${Date.now()}`;
    }
  }

  /**
   * @param {HTMLElement} currentContainer
   * @param {HTMLElement} updatedContent
   */
  #morphQuickViewElements(currentContainer, updatedContent) {
    // Preserve size chart container
    const currentSizeChart = currentContainer.querySelector('[data-size-chart-container]');

    // Morph SKU
    const currentSku = currentContainer.querySelector('[data-sku]');
    const updatedSku = updatedContent.querySelector('[data-sku]');
    if (currentSku instanceof HTMLElement && updatedSku instanceof HTMLElement) {
      morph(currentSku, updatedSku);
      // morph() uses childrenOnly:true so it never touches the element's own attributes.
      // If a prior call hid the SKU via style.display='none' (because that auto-resolved
      // variant had no SKU), we must clear it explicitly once the correct variant is back.
      currentSku.style.display = '';
    } else if (!currentSku && updatedSku instanceof HTMLElement) {
      const productDetails = currentContainer.querySelector('.quick-view__product-details');
      if (productDetails instanceof HTMLElement) {
        productDetails.insertBefore(updatedSku.cloneNode(true), productDetails.firstChild);
      }
    } else if (currentSku instanceof HTMLElement && !updatedSku) {
      currentSku.style.display = 'none';
    }

    const currentTitle = currentContainer.querySelector('.quick-view__title');
    const updatedTitle = updatedContent.querySelector('.quick-view__title');
    if (currentTitle instanceof HTMLElement && updatedTitle instanceof HTMLElement) {
      morph(currentTitle, updatedTitle);
    }

    // Restore size chart if removed
    if (currentSizeChart && !currentContainer.querySelector('[data-size-chart-container]')) {
      const insertTarget = currentContainer.querySelector('.quick-view__notify-me')?.parentElement || currentContainer;
      if (insertTarget instanceof HTMLElement) {
        insertTarget.appendChild(currentSizeChart);
      }
    }

    // Morph image
    const currentImage = currentContainer.querySelector('[data-quick-view-image]');
    const updatedImage = updatedContent.querySelector('[data-quick-view-image]');
    if (currentImage instanceof HTMLImageElement && updatedImage instanceof HTMLImageElement) {
      const newSrc = updatedImage.src;
      if (newSrc && newSrc !== currentImage.src) {
        currentImage.src = newSrc;
        if (updatedImage.srcset) currentImage.srcset = updatedImage.srcset;
        if (updatedImage.alt) currentImage.alt = updatedImage.alt;
        if (updatedImage.width) currentImage.width = updatedImage.width;
        if (updatedImage.height) currentImage.height = updatedImage.height;
        if (updatedImage.sizes) currentImage.sizes = updatedImage.sizes;
        currentImage.loading = 'eager';
        const finalSrc = currentImage.src;
        currentImage.src = '';
        requestAnimationFrame(() => { currentImage.src = finalSrc; });
      } else if (newSrc) {
        if (updatedImage.srcset) currentImage.srcset = updatedImage.srcset;
        if (updatedImage.alt) currentImage.alt = updatedImage.alt;
      }
    }

    // Morph pricing
    const currentPricing = currentContainer.querySelector('[data-pricing]');
    const updatedPricing = updatedContent.querySelector('[data-pricing]');
    if (currentPricing instanceof HTMLElement && updatedPricing instanceof HTMLElement) {
      const wasVisible = currentPricing.style.display !== 'none';
      morph(currentPricing, updatedPricing);
      if (wasVisible) currentPricing.style.display = '';
      
      // Apply price color based on clearance setting after morphing
      applyQuickViewPriceColor(currentContainer);
    }

    // Morph stock status
    const currentStock = currentContainer.querySelector('[data-stock-status]');
    const updatedStock = updatedContent.querySelector('[data-stock-status]');
    if (currentStock && updatedStock) {
      morph(currentStock, updatedStock);
    }

    // Morph shipping badge
    const currentBadge = currentContainer.querySelector('[data-shipping-badge]');
    const updatedBadge = updatedContent.querySelector('[data-shipping-badge]');
    if (currentBadge instanceof HTMLElement && updatedBadge instanceof HTMLElement) {
      morph(currentBadge, updatedBadge);
    } else if (!currentBadge && updatedBadge instanceof HTMLElement) {
      const insertPoint = currentContainer.querySelector('.elsklip-coupon-box') ||
                         currentContainer.querySelector('[data-quick-view-content-main]');
      if (insertPoint instanceof HTMLElement) {
        const clone = updatedBadge.cloneNode(true);
        if (clone instanceof HTMLElement) {
          insertPoint.insertAdjacentElement('afterend', clone);
        }
      }
    } else if (currentBadge instanceof HTMLElement && !updatedBadge) {
      currentBadge.remove();
    }

    // Update quantity selector using shared method
    this.#updateQuantitySelector(currentContainer, updatedContent);

    // Update selected option displays
    for (let i = 1; i <= 3; i++) {
      const current = currentContainer.querySelector(`[data-selected-${i}]`);
      const updated = updatedContent.querySelector(`[data-selected-${i}]`);
      if (current && updated) {
        morph(current, updated);
      } else if (current instanceof HTMLElement && !updated) {
        const checked = currentContainer.querySelector(`[data-option-position="${i}"]:checked`);
        if (checked instanceof HTMLInputElement) {
          current.textContent = checked.value;
        }
      }
    }

    // Morph variant selector - Let the server determine the correct selected values
    // After morph, preserve only the clicked option's state, let server determine others
    const currentSelector = currentContainer.querySelector('[data-variant-selector]');
    const updatedSelector = updatedContent.querySelector('[data-variant-selector]');
    if (currentSelector instanceof HTMLElement && updatedSelector instanceof HTMLElement) {
      // Save the clicked option's value before morph
      const clickedPosition = this.#lastClickedOptionPosition;
      const clickedValue = this.#lastClickedOptionValue;

      // Save Option 1 image swatch URLs before morph.
      // When an OOS combination is passed to the section rendering API, Shopify's
      // product_option_value.variant resolves incorrectly in that context — it may
      // return the same (snap-to) variant for ALL colors instead of the first variant
      // per color, causing every swatch to show the product's main image.
      // Preserving the images here ensures correct per-color thumbnails always show.
      /** @type {Map<string, {src: string, srcset: string, mainSrc: string, mainSrcset: string}>} */
      const swatchImageMap = new Map();
      currentSelector.querySelectorAll('.quick-view__swatches .quick-view__swatch-wrapper [data-option-value]').forEach((label) => {
        if (!(label instanceof HTMLElement)) return;
        const img = label.querySelector('.quick-view__swatch-img');
        if (img instanceof HTMLImageElement && img.src) {
          const key = `${label.dataset.optionPosition}::${label.dataset.optionValue}`;
          // Also compute main-image-resolution URLs (256px) by replacing the swatch width
          // in the Shopify CDN URL. The main quick-view image tag uses width: 256 / srcset 128+256.
          const mainSrc = img.src.replace(/\bwidth=\d+\b/, 'width=256');
          const mainSrcset = `${img.src.replace(/\bwidth=\d+\b/, 'width=128')} 128w, ${mainSrc} 256w`;
          swatchImageMap.set(key, { src: img.src, srcset: img.srcset || '', mainSrc, mainSrcset });
        }
      });

      morph(currentSelector, updatedSelector);

      // Restore swatch images after morph to fix OOS-context image corruption.
      if (swatchImageMap.size > 0) {
        currentSelector.querySelectorAll('.quick-view__swatches .quick-view__swatch-wrapper [data-option-value]').forEach((label) => {
          if (!(label instanceof HTMLElement)) return;
          const key = `${label.dataset.optionPosition}::${label.dataset.optionValue}`;
          const saved = swatchImageMap.get(key);
          if (!saved) return;
          const img = label.querySelector('.quick-view__swatch-img');
          if (img instanceof HTMLImageElement) {
            img.src = saved.src;
            if (saved.srcset) img.srcset = saved.srcset;
          } else {
            // Server returned a text-span fallback for this color — restore the img element.
            const imageSpan = label.querySelector('.quick-view__swatch-image');
            if (imageSpan instanceof HTMLElement) {
              const newImg = document.createElement('img');
              newImg.src = saved.src;
              if (saved.srcset) newImg.srcset = saved.srcset;
              newImg.className = 'quick-view__swatch-img';
              newImg.loading = 'lazy';
              imageSpan.innerHTML = '';
              imageSpan.appendChild(newImg);
            }
          }
        });
      }

      // After morph, ensure visual states are correct
      requestAnimationFrame(() => {
        currentSelector.querySelectorAll('[data-option-input]').forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          const label = input.closest('label');
          if (label instanceof HTMLElement) {
            // Remove all selected classes first
            label.classList.remove('quick-view__swatch--selected', 'quick-view__size-button--selected', 'quick-view__color-swatch--selected');
            
            // For the clicked option, preserve user's selection
            // For other options, use server's checked state
            let shouldBeChecked = input.checked;
            if (clickedPosition && input.dataset.optionPosition === clickedPosition) {
              shouldBeChecked = input.value === clickedValue;
              input.checked = shouldBeChecked;
            }
            
            // Add selected class if checked
            if (shouldBeChecked) {
              if (label.classList.contains('quick-view__swatch')) {
                label.classList.add('quick-view__swatch--selected');
              } else if (label.classList.contains('quick-view__color-swatch')) {
                label.classList.add('quick-view__color-swatch--selected');
              } else if (label.classList.contains('quick-view__size-button')) {
                label.classList.add('quick-view__size-button--selected');
              }
            }
          }
        });
        
        // Reset tracked values
        this.#lastClickedOptionPosition = null;
        this.#lastClickedOptionValue = null;

        // Sync main quick-view image for OOS combinations.
        //
        // When an OOS option combination is passed to Shopify's section-rendering API,
        // `selected_variant` snaps to the first available variant, so
        // `selected_variant.featured_media` returns that variant's image — not the image
        // for the color the user actually clicked.
        //
        // Fix: derive the correct 256px main-image URL from the swatch map (which was
        // captured before the morph, when every Option 1 color had its right thumbnail).
        // Shopify CDN supports arbitrary width via the `width=` query parameter, so we
        // can upscale the 100px swatch URL to match the main-image dimensions.
        if (swatchImageMap.size > 0) {
          const isOOS = currentContainer.querySelector('.quick-view__stock--out') !== null;
          if (isOOS) {
            const opt1Input = currentSelector.querySelector('[data-option-position="1"]:checked');
            const selectedOpt1Value = opt1Input instanceof HTMLInputElement ? opt1Input.value : null;
            if (selectedOpt1Value) {
              const savedImage = swatchImageMap.get(`1::${selectedOpt1Value}`);
              const mainImage = currentContainer.querySelector('[data-quick-view-image]');
              if (savedImage?.mainSrc && mainImage instanceof HTMLImageElement && mainImage.src !== savedImage.mainSrc) {
                mainImage.src = savedImage.mainSrc;
                mainImage.srcset = savedImage.mainSrcset || '';
              }
            }
          }
        }

      });
    }
  }

  /**
   * @param {HTMLInputElement} selectedInput
   * @param {HTMLElement} container
   */
  #updateSwatchSelectedState(selectedInput, container) {
    const optionPosition = selectedInput.dataset.optionPosition;
    if (!optionPosition) return;

    // CRITICAL: Clear ALL selected states FIRST before setting new one
    // This prevents race conditions when swatches are clicked rapidly
    container.querySelectorAll(`[data-option-position="${optionPosition}"]`).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      
      // Uncheck all radio buttons in this option group
      input.checked = false;
      
      const label = input.closest('.quick-view__swatch, .quick-view__size-button, .quick-view__color-swatch');
      if (label instanceof HTMLElement) {
        label.classList.remove('quick-view__swatch--selected', 'quick-view__size-button--selected', 'quick-view__color-swatch--selected');
      }
    });

    // Only add selected class and check the clicked input
    const selectedLabel = selectedInput.closest('.quick-view__swatch, .quick-view__size-button, .quick-view__color-swatch');
    if (selectedLabel instanceof HTMLElement) {
      if (selectedLabel.classList.contains('quick-view__swatch')) {
        selectedLabel.classList.add('quick-view__swatch--selected');
      } else if (selectedLabel.classList.contains('quick-view__color-swatch')) {
        selectedLabel.classList.add('quick-view__color-swatch--selected');
      } else {
        selectedLabel.classList.add('quick-view__size-button--selected');
      }
    }
    selectedInput.checked = true;
  }

  #initializeQuantitySelector() {
    this.#quantityUpdateHandler = (event) => {
      if (!(event instanceof QuantitySelectorUpdateEvent)) return;
      if (!event.target || !(event.target instanceof Node) || !this.contains(event.target)) return;

      const quantity = event.detail?.quantity;
      if (typeof quantity !== 'number') return;

      const quantityStr = quantity.toString();

      // Update ALL quantity inputs in main content (add-to-cart and shop pay forms)
      this.querySelectorAll('[data-quantity-input]').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = quantityStr;
        }
      });

      const quantityDisplay = this.querySelector('[data-quantity-value]');
      if (quantityDisplay instanceof HTMLElement) {
        quantityDisplay.textContent = quantityStr;
      }

      // Update ALL quantity inputs in footer (add-to-cart and shop pay forms)
      const drawerItemsComponent = this.closest('drawer-items-component');
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
      if (footerSlot) {
        footerSlot.querySelectorAll('[data-quantity-input]').forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.value = quantityStr;
          }
        });
      }
    };

    this.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#quantityUpdateHandler);
  }

  #initializeFormSubmission() {
    const productForm = this.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm instanceof HTMLFormElement) {
      if (productForm.dataset.formSubmissionPrevented === 'true') return;
      productForm.dataset.formSubmissionPrevented = 'true';

      productForm.addEventListener('submit', (e) => {
        if (this.#variantUpdateInProgress) {
          e.preventDefault();
          e.stopPropagation();
        }
      }, true);
    }

    this.#setupCartAddListener(this);
  }

  /**
   * Setup checkmark animation for add to cart buttons
   */
  #setupAddToCartCheckmarkAnimation() {
    // Attach to drawer-items-component to catch events from both content and footer
    const drawerItemsComponent = this.closest('drawer-items-component');
    const eventTarget = drawerItemsComponent || this;
    
    // Track active animations to cancel on error
    const activeAnimations = new Map();
    
    // Listen for cart errors to cancel animations and show error
    const errorHandler = (/** @type {CustomEvent} */ event) => {
      if (!(event instanceof CartErrorEvent)) return;
      
      // Find the button that triggered this error
      const sourceId = event.detail?.sourceId;
      const errorMessage = event.detail?.data?.message || 'Unable to add item to cart';
      
      // Find button by form ID or search in quick view context
      let addToCartButton = null;
      if (sourceId) {
        const form = document.getElementById(sourceId);
        if (form) {
          addToCartButton = form.querySelector('button[type="submit"][name="add"]');
        }
      }
      
      // Fallback: find button in quick view context
      if (!addToCartButton) {
        const buttons = (drawerItemsComponent || this).querySelectorAll('button[type="submit"][name="add"]');
        // Find the one that's currently processing
        buttons.forEach((btn) => {
          if (btn.classList.contains('add-to-cart-button--processing')) {
            addToCartButton = btn;
          }
        });
      }
      
      if (!addToCartButton) return;
      
      // Cancel any active animation
      const animationId = activeAnimations.get(addToCartButton);
      if (animationId) {
        clearTimeout(animationId.checkmarkTimeout);
        clearTimeout(animationId.resetTimeout);
        clearTimeout(animationId.restoreTimeout);
        activeAnimations.delete(addToCartButton);
      }
      
      // Reset button state immediately
      addToCartButton.classList.remove('add-to-cart-button--processing');
      const checkmarkElement = addToCartButton.querySelector('.add-to-cart-checkmark');
      if (checkmarkElement) {
        checkmarkElement.classList.remove('add-to-cart-checkmark--visible');
      }
      
      // Restore icon and text
      const iconElement = addToCartButton.querySelector('.add-to-cart-text .quick-view__action-icon');
      const textElement = addToCartButton.querySelector('[data-add-text]');
      if (iconElement) {
        iconElement.classList.remove('add-to-cart-icon--hidden');
      }
      if (textElement) {
        textElement.classList.remove('add-to-cart-text--hidden');
      }
      
      // Show error message
      this.#showAddToCartError(addToCartButton, errorMessage);
    };
    
    document.addEventListener(ThemeEvents.cartError, errorHandler);
    
    // Use capture phase to catch event before it's handled by add-to-cart-component
    eventTarget.addEventListener('click', (event) => {
      const target = /** @type {HTMLElement} */ (event.target);
      const addToCartButton = /** @type {HTMLButtonElement | null} */ (
        target.closest('button[type="submit"][name="add"]')
      );

      if (!addToCartButton || addToCartButton.disabled) return;

      // Check if button is within quick view context (component or drawer footer)
      const currentDrawerItems = addToCartButton.closest('drawer-items-component');
      const isInThisDrawer = currentDrawerItems === drawerItemsComponent || 
                             (drawerItemsComponent && drawerItemsComponent.contains(addToCartButton));
      const isInComponent = this.contains(addToCartButton);
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
      const isInFooter = footerSlot?.contains(addToCartButton);
      
      // Only handle if button is within this quick view drawer (component or footer)
      if (!isInThisDrawer && !isInComponent && !isInFooter) return;

      // Find icon element - icon is inside .add-to-cart-text with class quick-view__action-icon
      const iconElement = /** @type {HTMLElement | null} */ (
        addToCartButton.querySelector('.add-to-cart-text .quick-view__action-icon')
      );
      const textElement = /** @type {HTMLElement | null} */ (
        addToCartButton.querySelector('[data-add-text]')
      );

      if (!textElement) return;

      // Check if checkmark already exists
      let checkmarkElement = /** @type {HTMLElement | null} */ (
        addToCartButton.querySelector('.add-to-cart-checkmark')
      );

      if (!checkmarkElement) {
        // Create checkmark element
        checkmarkElement = document.createElement('span');
        checkmarkElement.className = 'add-to-cart-checkmark';
        checkmarkElement.innerHTML = `
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
            <path d="M20 6L9 17l-5-5"></path>
          </svg>
        `;
        addToCartButton.appendChild(checkmarkElement);
      }

      // Hide icon and text, then show checkmark with delay
      if (iconElement) {
        iconElement.classList.add('add-to-cart-icon--hidden');
      }
      textElement.classList.add('add-to-cart-text--hidden');
      addToCartButton.classList.add('add-to-cart-button--processing');

      // Store timeout IDs to allow cancellation
      const checkmarkTimeout = setTimeout(() => {
        checkmarkElement.classList.add('add-to-cart-checkmark--visible');
      }, 300); // Checkmark appears after 0.3s

      const resetTimeout = setTimeout(() => {
        checkmarkElement.classList.remove('add-to-cart-checkmark--visible');

        const restoreTimeout = setTimeout(() => {
          addToCartButton.classList.remove('add-to-cart-button--processing');
          if (iconElement) {
            iconElement.classList.remove('add-to-cart-icon--hidden');
          }
          textElement.classList.remove('add-to-cart-text--hidden');
          activeAnimations.delete(addToCartButton);
        }, 1000); // Wait for checkmark fade out (1s)
        
        // Update with restore timeout
        const current = activeAnimations.get(addToCartButton);
        if (current) {
          current.restoreTimeout = restoreTimeout;
        }
      }, 3000); // Total animation duration
      
      activeAnimations.set(addToCartButton, {
        checkmarkTimeout,
        resetTimeout,
        restoreTimeout: null
      });
    }, true); // Use capture phase
  }
  
  /**
   * Show error message in quick-view content area at the end
   * @param {HTMLButtonElement} button
   * @param {string} message
   */
  #showAddToCartError(button, message) {
    // Clear any existing error message timeout
    if (this.#errorMessageTimeout !== null) {
      clearTimeout(this.#errorMessageTimeout);
      this.#errorMessageTimeout = null;
    }

    // Find the quick-view element with data-quick-view-content attribute
    const quickViewContent = this.closest('[data-quick-view-content]');
    if (!quickViewContent) {
      console.warn('QuickView: Could not find quick-view content element for error message');
      return;
    }

    // Find or create error container at the end of quick-view content
    let errorContainer = quickViewContent.querySelector('[data-quick-view-error-container]');
    if (!errorContainer) {
      // Create error container if it doesn't exist
      errorContainer = document.createElement('div');
      errorContainer.setAttribute('data-quick-view-error-container', '');
      // Append to the end of quick-view content
      quickViewContent.appendChild(errorContainer);
    }

    // Remove ALL existing error messages within the quick-view content to prevent duplicates
    const existingErrors = quickViewContent.querySelectorAll('.add-to-cart-error-message');
    existingErrors.forEach(error => error.remove());

    // Create error message element
    const errorElement = document.createElement('div');
    errorElement.className = 'add-to-cart-error-message';
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');
    errorElement.setAttribute('aria-live', 'assertive');

    // Insert error message into the error container
    errorContainer.appendChild(errorElement);

    // Scroll error message into view
    errorElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Auto-hide after 5 seconds
    this.#errorMessageTimeout = setTimeout(() => {
      if (errorElement.parentNode) {
        errorElement.remove();
      }
      this.#errorMessageTimeout = null;
    }, 5000);
  }

  /**
   * @param {HTMLElement} container
   */
  #setupCartAddListener(container) {
    // The Add to Cart form lives in the drawer footer slot, which is rendered
    // OUTSIDE this quick-view-component (sibling of the scrollable content inside
    // drawer-items-component). So we resolve the product id from the footer form,
    // the content form, or the component's own data attribute — and always attach
    // the listener so the drawer closes on a successful add to cart.
    const drawerItemsComponent = container.closest('drawer-items-component');
    const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
    const productFormComponent =
      container.querySelector('product-form-component') ||
      footerSlot?.querySelector('product-form-component');

    const productId =
      (productFormComponent instanceof HTMLElement ? productFormComponent.dataset.productId : '') ||
      container.dataset.productId ||
      '';

    // Remove any previously attached handler to avoid duplicates when the form
    // submission is re-initialized (e.g. after a variant change).
    if (this.#cartAddHandler) {
      document.removeEventListener(CartAddEvent.eventName, this.#cartAddHandler);
      this.#cartAddHandler = null;
    }

    const handleCartAdd = (/** @type {CustomEvent} */ event) => {
      // Ignore failed add-to-cart attempts — keep the quick view open so the
      // user can correct the issue.
      if (event.detail?.data?.didError) return;

      const eventProductId = event.detail?.data?.productId;
      if (productId && eventProductId && eventProductId !== productId) return;

      const eventSource = event.detail?.data?.source;
      if (eventSource && eventSource !== 'product-form-component') return;

      const drawer = container.closest('drawer-component') || container.closest('[role="dialog"]');
      const closeQuickView = () => {
        if (drawer && 'closeDialog' in drawer && typeof drawer.closeDialog === 'function') {
          drawer.closeDialog();
        } else if (drawer && 'close' in drawer && typeof drawer.close === 'function') {
          drawer.close();
        }
      };

      // Close the quick view drawer ONLY AFTER the native cart drawer has opened,
      // so the two drawers do not animate at the same time (avoids the glitchy
      // effect of quick view closing while the mini cart is still sliding in).
      // The cart-drawer-component dispatches a non-bubbling 'dialog:open' event on
      // itself when it opens (via its auto-open behavior on CartAddEvent).
      const cartDrawer =
        document.querySelector('cart-drawer-component[auto-open]') ||
        document.querySelector('cart-drawer-component');

      if (cartDrawer instanceof HTMLElement) {
        /** @type {number | null} */
        let fallbackTimer = null;

        const onCartDrawerOpen = () => {
          cartDrawer.removeEventListener('dialog:open', onCartDrawerOpen);
          if (fallbackTimer) clearTimeout(fallbackTimer);
          closeQuickView();
        };

        cartDrawer.addEventListener('dialog:open', onCartDrawerOpen);

        // Safety fallback: if the cart drawer never opens (e.g. auto-open is
        // disabled), close the quick view anyway after a short delay.
        fallbackTimer = setTimeout(() => {
          cartDrawer.removeEventListener('dialog:open', onCartDrawerOpen);
          closeQuickView();
        }, 800);
      } else {
        closeQuickView();
      }

      document.removeEventListener(CartAddEvent.eventName, handleCartAdd);
      this.#cartAddHandler = null;
    };

    document.addEventListener(CartAddEvent.eventName, handleCartAdd);
    this.#cartAddHandler = handleCartAdd;
  }

  /**
   * Initialize notify me functionality
   * Binds footer button to form submission and checks notification status
   */
  #initializeNotifyMe() {
    const quickView = this.querySelector('[data-quick-view-content]') || this.querySelector('.quick-view') || this;
      if (!(quickView instanceof HTMLElement)) {
        return;
      }

    // Get the notify me form in the content area
    const notifyMeFormWrapper = quickView.querySelector('[data-notify-me-form-wrapper]');
    const notifyMeForm = notifyMeFormWrapper?.querySelector('quick-view-notify-me-form');
    
    // Get drawer items component for footer access
    const drawerItemsComponent = this.closest('drawer-items-component');
    const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
    const footerNotifyBtn = footerSlot?.querySelector('[data-notify-me]');

    if (!(notifyMeForm instanceof HTMLElement) || !footerNotifyBtn) {
      return;
    }

    // Initialize notify me form component
    this.#initializeNotifyMeFormComponent(notifyMeForm, footerNotifyBtn);

    // Check initial notification status on drawer open
    this.#checkAndUpdateNotificationStatus(quickView, footerSlot);

    // Footer button click handler - trigger form submission
    // Support both click and touchstart for iOS compatibility
    const newFooterBtn = footerSlot?.querySelector('[data-notify-me]');
    if (newFooterBtn instanceof HTMLButtonElement) {
      // Remove existing handlers by cloning (prevents duplicates)
      const newBtn = newFooterBtn.cloneNode(true);
      newFooterBtn.parentNode?.replaceChild(newBtn, newFooterBtn);
      
      // Handler function for both click and touch events
      /** @param {Event} e */
      const handleButtonAction = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Re-query the form in case it was morphed
        const currentForm = quickView.querySelector('quick-view-notify-me-form');
        if (!(currentForm instanceof HTMLElement)) return;
        
        // Check if already subscribed - prevent API call
        if (currentForm.dataset.subscribed === 'true') {
          return;
        }
        
        // Check if already submitting - prevent duplicate submissions
        if (this.#notifyMeSubmitting) {
          return;
        }
        
        // Set loading state on footer button
        if (newBtn instanceof HTMLButtonElement) {
          newBtn.classList.add('notify-me__submit--loading');
          newBtn.disabled = true;
          newBtn.setAttribute('aria-busy', 'true');
        }
        
        // Trigger the form submit button click
        // This will use the submit handler's isSubmitting flag
        const submitBtn = currentForm.querySelector('[data-notify-me-submit]');
        if (submitBtn instanceof HTMLButtonElement) {
          submitBtn.click();
        } else {
          // Remove loading state if submit button not found
          if (newBtn instanceof HTMLButtonElement) {
            newBtn.classList.remove('notify-me__submit--loading');
            newBtn.disabled = false;
            newBtn.removeAttribute('aria-busy');
          }
        }
      };
      
      // Add both click and touchstart for iOS compatibility
      newBtn.addEventListener('click', handleButtonAction);
      newBtn.addEventListener('touchstart', handleButtonAction, { passive: false });
    }
  }

  /**
   * Initialize notify me form component with submission handling
   * @param {HTMLElement} formElement - The quick-view-notify-me-form element
   * @param {Element | null | undefined} footerNotifyBtn - The footer notify me button
   */
  #initializeNotifyMeFormComponent(formElement, footerNotifyBtn) {
    // Prevent duplicate initialization - check if already initialized
    if (formElement.dataset.notifyMeInitialized === 'true') {
      return; // Already initialized, skip to prevent duplicate event listeners
    }
    
    // Mark as initialized
    formElement.dataset.notifyMeInitialized = 'true';

    const apiBaseUrl = formElement.dataset.apiBaseUrl || '/apps/notify-me/v1';
    const customerId = formElement.dataset.customerId;
    
    // Get input elements
    const nameInput = /** @type {HTMLInputElement | null} */ (formElement.querySelector('[data-notify-me-name]'));
    const emailInput = /** @type {HTMLInputElement | null} */ (formElement.querySelector('[data-notify-me-email]'));
    const skuInput = /** @type {HTMLInputElement | null} */ (formElement.querySelector('[data-notify-me-sku]'));
    const variantIdInput = /** @type {HTMLInputElement | null} */ (formElement.querySelector('[data-notify-me-variant-id]'));
    const submitBtn = formElement.querySelector('[data-notify-me-submit]');
    const fieldsContainer = formElement.querySelector('[data-notify-me-fields]');
    const thankYouContainer = formElement.querySelector('[data-notify-me-thank-you]');
    
    // Ensure customer email is populated if customer is logged in
    // This handles cases where the form is morphed and values might be reset
    if (customerId && emailInput instanceof HTMLInputElement) {
      // Check if email input is empty but has readonly attribute (should have value if customer is logged in)
      if (!emailInput.value && emailInput.hasAttribute('readonly')) {
        // Try to get customer email from window.theme or other global sources
        const theme = /** @type {any} */ (window).theme;
        if (theme?.customer?.email) {
          emailInput.value = theme.customer.email;
        }
      }
    }

    // Real-time validation using shared functions
    if (nameInput) {
      nameInput.addEventListener('blur', () => validateNotifyMeNameField(nameInput, formElement));
      nameInput.addEventListener('input', () => {
        clearNotifyMeFieldError(nameInput);
        // Update button state when name field changes
        this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
      });
    }

    if (emailInput) {
      emailInput.addEventListener('blur', () => validateNotifyMeEmailField(emailInput, formElement));
      emailInput.addEventListener('input', () => {
        clearNotifyMeFieldError(emailInput);
        // Update button state when email field changes
        this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
      });
    }

    // Initial button state check after form is set up
    this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);

    // Submit handler - use class-level flag to prevent duplicate submissions
    if (submitBtn) {
      // Remove existing handler if present (prevent duplicates)
      if (this.#notifyMeSubmitHandler && submitBtn instanceof HTMLElement) {
        submitBtn.removeEventListener('click', this.#notifyMeSubmitHandler);
      }
      
      this.#notifyMeSubmitHandler = async () => {
        // Prevent duplicate submissions using class-level flag
        if (this.#notifyMeSubmitting) {
        return;
      }

        // Check if already subscribed - prevent API call
        if (formElement.dataset.subscribed === 'true') {
          return;
        }
        
        // Validate fields using shared functions
        const isNameValid = !nameInput || validateNotifyMeNameField(nameInput, formElement);
        const isEmailValid = !emailInput || validateNotifyMeEmailField(emailInput, formElement);

        if (!isNameValid || !isEmailValid) {
          // Remove loading state when validation fails
          this.#setFooterButtonLoadingState(footerNotifyBtn, false);
          // Update button state based on field values
          this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
          // Scroll to form to show validation errors
          this.#scrollToNotifyMeForm(formElement);
          return;
        }

        // Collect form data
        const sku = skuInput?.value || '';
        const variantId = variantIdInput?.value || '';
        const email = emailInput?.value.trim() || '';
        const name = nameInput?.value.trim() || '';

        if (!sku || !email) {
          this.#showNotifyMeMessage(formElement, getNotifyMeTranslation(formElement, 'error_validation', 'Please fill in all required fields'), 'error');
          // Remove loading state when validation fails
          this.#setFooterButtonLoadingState(footerNotifyBtn, false);
          // Update button state based on field values
          this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
          // Scroll to form to show error message
          this.#scrollToNotifyMeForm(formElement);
          return;
        }

        // Set submitting flag
        this.#notifyMeSubmitting = true;
        
        // Set loading state on footer button
        this.#setFooterButtonLoadingState(footerNotifyBtn, true);
        
        // Cancel any pending request
        this.#notifyMeAbortController?.abort();
        this.#notifyMeAbortController = new AbortController();

        // Parse name using shared utility
        const { firstName, lastName } = splitName(name);

        try {
          // Submit to API using shared function
          const response = await submitNotifyMeNotification(
            {
              sku,
              email,
              customerFirstName: firstName,
              customerLastName: lastName,
              variantId,
              customerId: customerId || undefined,
            },
            apiBaseUrl,
            this.#notifyMeAbortController
          );

          /** @type {any} */
          const data = response.data;

          // Handle success cases: 200 OK, 201 Created, or 409 Conflict (already exists)
          // 409 Conflict means the notification already exists, which is a success case
          if (data.success || data.code === 'OK' || data.code === 'NOTIFICATION_EXISTS' || response.status === 200 || response.status === 201 || response.status === 409) {
            // Extract message from API response (check multiple possible locations)
            const apiMessage = data.message || 
                              (data.data && typeof data.data === 'object' && data.data.message ? data.data.message : null) || 
                              (data.data && typeof data.data === 'object' && data.data.notification && typeof data.data.notification === 'object' && data.data.notification.message ? data.data.notification.message : null) || 
                              null;
            
            // Success - show message from API response (or fallback to static message)
            this.#setNotifyMeSubscribed(formElement, footerNotifyBtn, true, apiMessage);
            
            // Update bell icons for this SKU (pass quickView for parent swatch propagation)
            const quickView = this.querySelector('[data-quick-view-content]') || this.querySelector('.quick-view') || this;
            this.#updateBellIconsForSku(sku, true, quickView);
          } else {
            // Error - use shared error message function
            const errorMessage = getNotifyMeErrorMessage(formElement, data);
            this.#showNotifyMeMessage(formElement, errorMessage, 'error');
            // Remove loading state on error
            this.#setFooterButtonLoadingState(footerNotifyBtn, false);
            // Update button state based on field values
            this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
            // Scroll to form to show error message
            this.#scrollToNotifyMeForm(formElement);
          }
        } catch (error) {
          // Ignore abort errors
          if (error.name === 'AbortError') {
            // Remove loading state on abort
            this.#setFooterButtonLoadingState(footerNotifyBtn, false);
            // Update button state based on field values
            this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
            return;
          }
          this.#showNotifyMeMessage(formElement, getNotifyMeTranslation(formElement, 'error_network', 'Network error. Please try again.'), 'error');
          // Remove loading state on error
          this.#setFooterButtonLoadingState(footerNotifyBtn, false);
          // Update button state based on field values
          this.#updateNotifyMeButtonState(formElement, footerNotifyBtn);
          // Scroll to form to show error message
          this.#scrollToNotifyMeForm(formElement);
        } finally {
          // Reset submission flag
          this.#notifyMeSubmitting = false;
        }
      };
      
      // Add event listener
      submitBtn.addEventListener('click', this.#notifyMeSubmitHandler);
    }

    // Check if customer is logged in and variant is already subscribed
    // Only check if not already checked (prevent duplicate API calls)
    if (customerId && skuInput?.value && formElement.dataset.notifyMeChecked !== 'true') {
      formElement.dataset.notifyMeChecked = 'true';
      this.#checkExistingNotification(formElement, apiBaseUrl, skuInput.value, footerNotifyBtn);
    }
  }

  /**
   * Check if user already has a notification for this SKU
   * @param {HTMLElement} formElement
   * @param {string} apiBaseUrl
   * @param {string} sku
   * @param {Element | null | undefined} footerNotifyBtn
   */
  async #checkExistingNotification(formElement, apiBaseUrl, sku, footerNotifyBtn) {
    const quickView = this.#findQuickViewContainer(this);
    if (!(quickView instanceof HTMLElement)) return;
    
    const isNotified = await this.#checkSingleVariantNotificationStatus(sku, quickView, apiBaseUrl);
    
    if (isNotified) {
      this.#setNotifyMeSubscribed(formElement, footerNotifyBtn, true);
      // Update bell icons for this SKU
      this.#updateBellIconsForSku(sku, true, quickView);
    }
  }

  /**
   * Check single variant notification status via API
   * @param {string} sku
   * @param {HTMLElement} quickView
   * @param {string} apiBaseUrl
   * @returns {Promise<boolean>}
   */
  async #checkSingleVariantNotificationStatus(sku, quickView, apiBaseUrl) {
    if (!sku || !sku.trim()) {
      return false;
    }

    // Prevent multiple simultaneous API calls
    if (this.#notifyMeCheckInProgress) {
      return false;
    }
    
    // Clear any pending timeout
    if (this.#notifyMeCheckTimeout) {
      clearTimeout(this.#notifyMeCheckTimeout);
      this.#notifyMeCheckTimeout = null;
    }
    
    // Debounce the API call to prevent rapid successive calls
    return new Promise((/** @type {(value: boolean) => void} */ resolve) => {
      this.#notifyMeCheckTimeout = setTimeout(async () => {
        this.#notifyMeCheckInProgress = true;
        
        try {
          // Call API with single SKU: sku=[SKU]
          const response = await fetch(`${apiBaseUrl}/list?sku=[${sku.trim()}]`);
          const data = await response.json();

          if (data.success && data.data?.results) {
            const existingNotification = data.data.results.find(
              /** @param {{ sku: string }} n */
              (n) => n.sku === sku.trim()
            );
            
            this.#notifyMeCheckInProgress = false;
            resolve(!!existingNotification);
          } else {
            this.#notifyMeCheckInProgress = false;
            resolve(false);
          }
        } catch (error) {
          this.#notifyMeCheckInProgress = false;
          resolve(false);
        }
      }, 100);
    });
  }

  /**
   * Check and update notification status on drawer open
   * @param {HTMLElement} quickView
   * @param {Element | null | undefined} footerSlot
   */
  async #checkAndUpdateNotificationStatus(quickView, footerSlot) {
    const notifyMeForm = quickView.querySelector('quick-view-notify-me-form');
    const footerNotifyBtn = footerSlot?.querySelector('[data-notify-me]');
    const notifyMeWrapper = footerSlot?.querySelector('.notify_me_wrapper');
    
    if (!(notifyMeForm instanceof HTMLElement)) {
      return;
    }
    
    // Hide button wrapper immediately to prevent any visual flickering
    if (notifyMeWrapper instanceof HTMLElement) {
      notifyMeWrapper.style.opacity = '0';
      notifyMeWrapper.style.visibility = 'hidden';
      notifyMeWrapper.style.transition = 'none'; // Disable transitions during state change
    }
    
    const customerId = notifyMeForm.dataset.customerId;
    const apiBaseUrl = notifyMeForm.dataset.apiBaseUrl || '/apps/notify-me/v1';
    const skuInput = /** @type {HTMLInputElement | null} */ (notifyMeForm.querySelector('[data-notify-me-sku]'));
    const sku = skuInput?.value;

    if (customerId && sku) {
      // Check if there's a pending restore state from footer reload
      const pendingRestore = this.dataset.pendingNotifyMeRestore === 'true';
      const pendingSku = this.dataset.pendingNotifyMeSku;
      
      // If SKU matches pending restore, restore state immediately
      if (pendingRestore && pendingSku === sku) {
        // Wait for state to be set before showing button
        const btn = footerNotifyBtn instanceof HTMLElement ? footerNotifyBtn : null;
        await this.#setNotifyMeSubscribedAndWait(notifyMeForm, btn, true);
        // Clear pending restore state
        delete this.dataset.pendingNotifyMeRestore;
        delete this.dataset.pendingNotifyMeSku;
        // Update bell icons for this SKU
        this.#updateBellIconsForSku(sku, true, quickView);
        // Show button after state is fully set
        this.#showNotifyMeButton(notifyMeWrapper);
        return;
      }
      
      // Otherwise check notification status for selected variant
      const isNotified = await this.#checkSingleVariantNotificationStatus(sku, quickView, apiBaseUrl);
      
      const btn = footerNotifyBtn instanceof HTMLElement ? footerNotifyBtn : null;
      if (isNotified) {
        // Wait for state to be set before showing button
        await this.#setNotifyMeSubscribedAndWait(notifyMeForm, btn, true);
        // Update bell icons for this SKU
        this.#updateBellIconsForSku(sku, true, quickView);
      } else {
        // Reset to "Notify Me" state and wait for it to complete
        await this.#setNotifyMeSubscribedAndWait(notifyMeForm, btn, false);
        // Clear bell icons from all swatches (so previous variant's bell is removed in multi-option)
        this.#updateBellIconsForSku(sku, false, quickView);
        // Clear any pending restore state if variant changed
        if (pendingRestore) {
          delete this.dataset.pendingNotifyMeRestore;
          delete this.dataset.pendingNotifyMeSku;
        }
        // Note: Button state is already updated by #setNotifyMeSubscribed when unsubscribing
      }
    } else {
      // No customer or SKU - reset to default state
      const btn = footerNotifyBtn instanceof HTMLElement ? footerNotifyBtn : null;
      await this.#setNotifyMeSubscribedAndWait(notifyMeForm, btn, false);
      // Note: Button state is already updated by #setNotifyMeSubscribed when unsubscribing
    }
    
    // Show button after all state updates complete (prevent flickering)
    this.#showNotifyMeButton(notifyMeWrapper);
  }

  /**
   * Show notify me button wrapper with smooth transition
   * @param {Element | HTMLElement | null | undefined} notifyMeWrapper
   */
  #showNotifyMeButton(notifyMeWrapper) {
    if (notifyMeWrapper instanceof HTMLElement) {
      // Use double requestAnimationFrame to ensure all DOM updates are complete
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          notifyMeWrapper.style.transition = ''; // Re-enable transitions
          notifyMeWrapper.style.opacity = '';
          notifyMeWrapper.style.visibility = '';
        });
      });
    }
  }

  /**
   * Set notify me subscribed state and wait for DOM updates to complete
   * @param {HTMLElement} formElement
   * @param {Element | null | undefined} footerNotifyBtn
   * @param {boolean} isSubscribed
   * @param {string | null | undefined} apiMessage
   * @returns {Promise<void>}
   */
  async #setNotifyMeSubscribedAndWait(formElement, footerNotifyBtn, isSubscribed, apiMessage = null) {
    return new Promise((resolve) => {
      // Set the state synchronously first
      // Cast footerNotifyBtn to HTMLElement for the method call
      this.#setNotifyMeSubscribed(formElement, footerNotifyBtn instanceof HTMLElement ? footerNotifyBtn : null, isSubscribed, apiMessage);
      
      // Wait for requestAnimationFrame to complete (ensures DOM updates are done)
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          resolve();
        });
      });
    });
  }

  /**
   * Set notify me form to subscribed state
   * @param {HTMLElement} formElement
   * @param {Element | null | undefined} footerNotifyBtn
   * @param {boolean} isSubscribed
   * @param {string | null | undefined} apiMessage - Optional message from API response
   */
  #setNotifyMeSubscribed(formElement, footerNotifyBtn, isSubscribed, apiMessage = null) {
    if (isSubscribed) {
      formElement.dataset.subscribed = 'true';
      
      // Hide fields, show thank you
      const fieldsContainer = formElement.querySelector('[data-notify-me-fields]');
      const thankYouContainer = formElement.querySelector('[data-notify-me-thank-you]');
      
      if (fieldsContainer instanceof HTMLElement) {
        fieldsContainer.hidden = true;
      }
      if (thankYouContainer instanceof HTMLElement) {
        thankYouContainer.hidden = false;
        
        // Update thank you message text with API response message if available
        const thankYouText = thankYouContainer.querySelector('.quick-view-notify-me__thank-you-text');
        if (thankYouText instanceof HTMLElement) {
          if (apiMessage && apiMessage.trim()) {
            // Use message from API response
            thankYouText.textContent = apiMessage.trim();
          } else {
            // Fallback to static translation using shared function
            const fallbackMessage = getNotifyMeTranslation(formElement, 'thank_you_message', "Thank you! You'll receive a notification once this item is back in stock.");
            thankYouText.textContent = fallbackMessage;
          }
        }
      }

      // Remove loading state before updating button text
      this.#setFooterButtonLoadingState(footerNotifyBtn, false);
      
      // Update footer button text
      // Re-query the footer button to ensure we have the current element (in case it was morphed)
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const drawerItemsComponent = this.closest('drawer-items-component');
        const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
        const quickViewFooter = footerSlot?.querySelector('[data-quick-view-footer]');
        const currentFooterNotifyBtn = quickViewFooter?.querySelector('[data-notify-me]') || footerSlot?.querySelector('[data-notify-me]') || footerNotifyBtn;
        
        if (currentFooterNotifyBtn instanceof HTMLElement) {
          // Always show "Thank you!" in the button when subscribed (regardless of API message)
          // The API message is shown in the thank you container, not in the button
          const buttonText = 'Thank you!';
          
          // Try to find the text span first
          const textSpan = currentFooterNotifyBtn.querySelector('[data-notify-me-text]');
          if (textSpan instanceof HTMLElement) {
            textSpan.textContent = buttonText;
          } else {
            // Fallback: set text directly on button if span not found
            const firstSpan = currentFooterNotifyBtn.querySelector('span');
            if (firstSpan instanceof HTMLElement) {
              firstSpan.textContent = buttonText;
            } else {
              // Last resort: set textContent on button itself
              currentFooterNotifyBtn.textContent = buttonText;
            }
          }
          
          currentFooterNotifyBtn.classList.add('notify-me--subscribed');
          currentFooterNotifyBtn.setAttribute('disabled', 'disabled');
        }
      });
    } else {
      formElement.dataset.subscribed = 'false';
      
      // Show fields, hide thank you
      const fieldsContainer = formElement.querySelector('[data-notify-me-fields]');
      const thankYouContainer = formElement.querySelector('[data-notify-me-thank-you]');
      
      if (fieldsContainer instanceof HTMLElement) {
        fieldsContainer.hidden = false;
      }
      if (thankYouContainer instanceof HTMLElement) {
        thankYouContainer.hidden = true;
      }

      // Reset footer button text to "Notify Me"
      // Re-query the footer button to ensure we have the current element (in case it was morphed)
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        const drawerItemsComponent = this.closest('drawer-items-component');
        const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
        const quickViewFooter = footerSlot?.querySelector('[data-quick-view-footer]');
        const currentFooterNotifyBtn = quickViewFooter?.querySelector('[data-notify-me]') || footerSlot?.querySelector('[data-notify-me]') || footerNotifyBtn;
        
        if (currentFooterNotifyBtn instanceof HTMLElement) {
          // Get original text from translations or default
          const theme = /** @type {any} */ (window).theme;
          const notifyMeText = theme?.strings?.notifyMe || 'Notify Me';
          
          // Try to find the text span first
          const buttonText = currentFooterNotifyBtn.querySelector('[data-notify-me-text]');
          if (buttonText instanceof HTMLElement) {
            buttonText.textContent = notifyMeText;
          } else {
            // Fallback: set text directly on button if span not found
            const firstSpan = currentFooterNotifyBtn.querySelector('span');
            if (firstSpan instanceof HTMLElement) {
              firstSpan.textContent = notifyMeText;
            } else {
              // Last resort: set textContent on button itself
              currentFooterNotifyBtn.textContent = notifyMeText;
            }
          }
          
          currentFooterNotifyBtn.classList.remove('notify-me--subscribed');
          currentFooterNotifyBtn.removeAttribute('disabled');
          
          // Update button state based on field values after unsubscribing
          this.#updateNotifyMeButtonState(formElement, currentFooterNotifyBtn);
        }
      });
    }
  }

  /**
   * Set drawer-notify-msg class on dialog element (similar to drawer-cart-error)
   * @param {boolean} isNotified - Whether to add or remove the class
   */
  #setDrawerNotifiedClass(isNotified) {
    // Find the drawer component
    const drawerComponent = this.closest('drawer-component');
    if (!drawerComponent) {
      return;
    }
    // Find the dialog element by class drawer__dialog
    const dialog = drawerComponent.querySelector('.drawer__dialog') || drawerComponent.querySelector('dialog');
    if (!(dialog instanceof HTMLElement)) {
      return;
    }
    if (isNotified) {
      dialog.classList.add('drawer-notify-msg');
    } else {
      dialog.classList.remove('drawer-notify-msg');
    }
  }

  /**
   * Show drawer loader during variant selection and footer rendering
   * @param {HTMLElement | null | undefined} container
   */
  #showDrawerLoader(container) {
    const drawerItemsComponent = container?.closest('drawer-items-component');
    const drawerLoader = drawerItemsComponent?.querySelector('[data-drawer-loader]');
    if (drawerLoader instanceof HTMLElement) {
      drawerLoader.classList.remove('hidden');
      drawerLoader.setAttribute('aria-hidden', 'false');
      drawerLoader.setAttribute('aria-busy', 'true');
    }
  }

  /**
   * Set loading state on footer notify me button
   * @param {Element | null | undefined} footerNotifyBtn
   * @param {boolean} isLoading
   */
  #setFooterButtonLoadingState(footerNotifyBtn, isLoading) {
    // Re-query the footer button to ensure we have the current element (in case it was morphed)
    requestAnimationFrame(() => {
      const drawerItemsComponent = this.closest('drawer-items-component');
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
      const quickViewFooter = footerSlot?.querySelector('[data-quick-view-footer]');
      const currentFooterNotifyBtn = quickViewFooter?.querySelector('[data-notify-me]') || footerSlot?.querySelector('[data-notify-me]') || footerNotifyBtn;
      
      if (currentFooterNotifyBtn instanceof HTMLButtonElement) {
        if (isLoading) {
          currentFooterNotifyBtn.classList.add('notify-me__submit--loading');
          currentFooterNotifyBtn.disabled = true;
          currentFooterNotifyBtn.setAttribute('aria-busy', 'true');
        } else {
          currentFooterNotifyBtn.classList.remove('notify-me__submit--loading');
          // Don't automatically enable - let #updateNotifyMeButtonState handle it based on field values
          // Button state will be updated by #updateNotifyMeButtonState after loading completes
          currentFooterNotifyBtn.removeAttribute('aria-busy');
        }
      }
    });
  }

  /**
   * Update notify me button state based on form field values
   * Button is enabled only when both name and email fields have values
   * @param {HTMLElement} formElement
   * @param {Element | null | undefined} footerNotifyBtn
   */
  #updateNotifyMeButtonState(formElement, footerNotifyBtn) {
    // Don't update if form is in subscribed state (showing "Thank you")
    if (formElement.dataset.subscribed === 'true') {
      return;
    }

    // Re-query the footer button to ensure we have the current element (in case it was morphed)
    requestAnimationFrame(() => {
      const drawerItemsComponent = this.closest('drawer-items-component');
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
      const quickViewFooter = footerSlot?.querySelector('[data-quick-view-footer]');
      const currentFooterNotifyBtn = quickViewFooter?.querySelector('[data-notify-me]') || footerSlot?.querySelector('[data-notify-me]') || footerNotifyBtn;
      
      if (!(currentFooterNotifyBtn instanceof HTMLButtonElement)) {
        return;
      }

      // Get current form inputs (re-query in case form was morphed)
      const currentForm = this.querySelector('quick-view-notify-me-form');
      if (!(currentForm instanceof HTMLElement)) {
        return;
      }

      const nameInput = currentForm.querySelector('[data-notify-me-name]');
      const emailInput = currentForm.querySelector('[data-notify-me-email]');

      // Check if both fields have values
      const nameValue = nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '';
      const emailValue = emailInput instanceof HTMLInputElement ? emailInput.value.trim() : '';
      
      const bothFieldsHaveValues = nameValue !== '' && emailValue !== '';

      // Enable/disable button based on field values
      // Also check if button is in subscribed state (should remain disabled)
      if (bothFieldsHaveValues && currentFooterNotifyBtn.dataset.subscribed !== 'true') {
        currentFooterNotifyBtn.disabled = false;
        currentFooterNotifyBtn.removeAttribute('aria-disabled');
      } else {
        currentFooterNotifyBtn.disabled = true;
        currentFooterNotifyBtn.setAttribute('aria-disabled', 'true');
      }
    });
  }

  /**
   * Update bell icons and ring icons for a specific SKU
   * Also propagates is-notified to parent selected swatches if this is the last option.
   * For multi-option (4+ options): always clears all is-notified first, then adds only for this SKU when isNotified is true.
   * @param {string} sku
   * @param {boolean} isNotified
   * @param {HTMLElement} [quickView]
   */
  #updateBellIconsForSku(sku, isNotified, quickView) {
    if (!quickView) {
      quickView = this.querySelector('[data-quick-view-content]') || this.querySelector('.quick-view') || this;
    }
    
    if (!(quickView instanceof HTMLElement)) {
      return;
    }

    // Clear is-notified from ALL swatches first (fixes multi-option: bell stays on previous variant)
    // Scoped to quickView so standard and multi-option swatches both reset on variant change
    quickView.querySelectorAll('.quick-view__swatch-wrapper.is-notified, .quick-view__swatch-icon--ring.is-notified').forEach((el) => {
      el.classList.remove('is-notified');
    });
    
    if (!isNotified) {
      return;
    }
    
    // Find the selected swatch by finding the checked input for the last option
    // This is more reliable than matching by SKU because the DOM SKU might differ from the selected variant's SKU
    const allLastOptionInputs = quickView.querySelectorAll('[data-last-option-swatch] input[data-option-input]:checked');
    
    // If no checked input found, try to find by SKU as fallback
    /** @type {HTMLElement[]} */
    let lastOptionSwatches = [];
    
    if (allLastOptionInputs.length > 0) {
      // Get the label (swatch) for the checked input
      allLastOptionInputs.forEach((/** @type {HTMLInputElement} */ input) => {
        const label = input.closest('[data-last-option-swatch]');
        if (label instanceof HTMLElement) {
          lastOptionSwatches.push(label);
        }
      });
    } else {
      // Fallback: Try to find by SKU if no checked input found
      // This might happen if the variant was selected programmatically
      if (sku && typeof sku === 'string') {
        const skuTrimmed = sku.trim();
        if (skuTrimmed) {
          const allLastOptionSwatches = quickView.querySelectorAll('[data-last-option-swatch]');
          lastOptionSwatches = Array.from(allLastOptionSwatches)
            .filter((label) => {
              if (!(label instanceof HTMLElement)) return false;
              const labelSku = label.getAttribute('data-variant-sku');
              return labelSku && labelSku.trim() === skuTrimmed;
            })
            .map((label) => /** @type {HTMLElement} */ (label));
        }
      }
    }
    lastOptionSwatches.forEach((/** @type {HTMLElement} */ label) => {
      if (!(label instanceof HTMLElement)) return;
      
      // Find the wrapper (parent) and ring icon (sibling of the label)
      const wrapper = label.parentElement;
      const ringIcon = wrapper?.querySelector('.quick-view__swatch-icon--ring');
      
      if (isNotified) {
        // Add is-notified class to wrapper (parent) - same as variant-main-picker.liquid pattern
        if (wrapper instanceof HTMLElement) {
          wrapper.classList.add('is-notified');
        }
        // Add is-notified to ring icon div (besides quick-view__swatch-icon--ring class)
        if (ringIcon instanceof HTMLElement) {
          ringIcon.classList.add('is-notified');
        }
        
        // PROPAGATE: If this is the last option swatch, also add is-notified to parent selected swatches
        const optionPosition = label.getAttribute('data-option-position');
        if (optionPosition) {
          const optionPos = parseInt(optionPosition, 10);
          // Find all previous options (options before this one)
          for (let prevPos = 1; prevPos < optionPos; prevPos++) {
            // Find the selected swatch for this previous option using selected class
            // Check for different swatch types: image swatches, color swatches, size buttons
            const selectedSwatch = quickView.querySelector(
              `[data-option-position="${prevPos}"].quick-view__swatch--selected, ` +
              `[data-option-position="${prevPos}"].quick-view__color-swatch--selected, ` +
              `[data-option-position="${prevPos}"].quick-view__size-button--selected`
            );
            
            if (selectedSwatch instanceof HTMLElement) {
              const prevWrapper = selectedSwatch.parentElement;
              const prevRingIcon = prevWrapper?.querySelector('.quick-view__swatch-icon--ring');
              
              // Check if ring icon exists, then add is-notified to it
              if (prevRingIcon instanceof HTMLElement) {
                // Add is-notified to parent swatch wrapper
                if (prevWrapper instanceof HTMLElement) {
                  prevWrapper.classList.add('is-notified');
                }
                // Add is-notified to ring icon div
                prevRingIcon.classList.add('is-notified');
              }
            }
          }
        }
      } else {
        // Remove is-notified class from wrapper (parent)
        if (wrapper instanceof HTMLElement) {
          wrapper.classList.remove('is-notified');
        }
        // Remove from ring icon
        if (ringIcon instanceof HTMLElement) {
          ringIcon.classList.remove('is-notified');
        }
        
        // PROPAGATE: Remove is-notified from parent selected swatches
        const optionPosition = label.getAttribute('data-option-position');
        if (optionPosition) {
          const optionPos = parseInt(optionPosition, 10);
          // Find all previous options (options before this one)
          for (let prevPos = 1; prevPos < optionPos; prevPos++) {
            // Find the selected swatch for this previous option using selected class
            const selectedSwatch = quickView.querySelector(
              `[data-option-position="${prevPos}"].quick-view__swatch--selected, ` +
              `[data-option-position="${prevPos}"].quick-view__color-swatch--selected, ` +
              `[data-option-position="${prevPos}"].quick-view__size-button--selected`
            );
            
            if (selectedSwatch instanceof HTMLElement) {
              const prevWrapper = selectedSwatch.parentElement;
              const prevRingIcon = prevWrapper?.querySelector('.quick-view__swatch-icon--ring');
              
              // Check if ring icon exists, then remove is-notified from it
              if (prevRingIcon instanceof HTMLElement) {
                // Remove is-notified from parent swatch wrapper
                if (prevWrapper instanceof HTMLElement) {
                  prevWrapper.classList.remove('is-notified');
                }
                // Remove is-notified from ring icon div
                prevRingIcon.classList.remove('is-notified');
              }
            }
          }
        }
      }
    });
  }

  /**
   * Show message in notify me form
   * @param {HTMLElement} formElement
   * @param {string} message
   * @param {'success' | 'error'} type
   */
  #showNotifyMeMessage(formElement, message, type) {
    // Remove existing message
    const existingMessage = formElement.querySelector('.quick-view-notify-me__message');
    existingMessage?.remove();

    // Create new message
    const messageElement = document.createElement('div');
    messageElement.className = `quick-view-notify-me__message quick-view-notify-me__message--${type}`;
    messageElement.textContent = message;
    messageElement.setAttribute('role', 'alert');
    
    // Insert at top of form
    formElement.prepend(messageElement);

    // Auto-hide after 5 seconds
    setTimeout(() => {
      messageElement.remove();
    }, 5000);
  }

  /**
   * Scroll to notify me form to show validation errors or messages
   * @param {HTMLElement} formElement
   */
  #scrollToNotifyMeForm(formElement) {
    if (!(formElement instanceof HTMLElement)) return;

    // Double rAF ensures DOM has fully painted error messages before measuring
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const drawerItemsComponent = formElement.closest('drawer-items-component');
        const scrollHint = drawerItemsComponent?.querySelector('scroll-hint.drawer__items');
        const drawerContent = drawerItemsComponent?.querySelector('.drawer__content');
        const dialog = drawerItemsComponent?.closest('drawer-component')?.querySelector('dialog');

        const scrollContainer = scrollHint || drawerContent || dialog || drawerItemsComponent;

        if (!(scrollContainer instanceof HTMLElement)) return;

        // Scroll to the FIRST invalid field, not the footer slot
        const firstErrorField = formElement.querySelector('.quick-view-notify-me__input--error');
        const targetElement = firstErrorField || formElement;

        const targetRect = targetElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        const scrollTop = scrollContainer.scrollTop;
        const targetTopRelative = targetRect.top - containerRect.top + scrollTop;
        const offset = 40;
        const targetScroll = targetTopRelative - offset;

        scrollContainer.scrollTo({
          top: Math.max(0, targetScroll),
          behavior: 'smooth'
        });
      });
    });
  }

  /**
   * @param {HTMLElement} button
   */
  #initializeSizeChart(button) {
    if (!button) return;

    button.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const quickView = this.querySelector('[data-quick-view-content]') || this.querySelector('.quick-view') || this;
      if (quickView instanceof HTMLElement) {
        this.#showSizeChart(quickView);
      }
    });

    const backButton = this.querySelector('.quick-view__size-chart-back');
    if (backButton instanceof HTMLElement) {
      backButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const quickView = this.querySelector('[data-quick-view-content]') || this.querySelector('.quick-view') || this;
        if (quickView instanceof HTMLElement) {
          this.#hideSizeChart(quickView);
        }
      });
    }
  }

  /**
   * @param {HTMLElement} quickView
   */
  #showSizeChart(quickView) {
    const sizeChartContainer = quickView.querySelector('[data-size-chart-container]');
    const mainContent = quickView.querySelector('[data-quick-view-content-main]');
    const drawerItemsComponent = quickView.closest('drawer-items-component');
    const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');

    if (!sizeChartContainer || !mainContent) {
      console.error('Quick View: Size chart or main content not found');
      return;
    }

    if (mainContent instanceof HTMLElement) mainContent.style.display = 'none';
    if (footerSlot instanceof HTMLElement) footerSlot.style.display = 'none';
    if (sizeChartContainer instanceof HTMLElement) {
      sizeChartContainer.removeAttribute('hidden');
      sizeChartContainer.style.display = 'block';
    }

    // Reset scroll position to top when size chart opens
    // Use requestAnimationFrame to ensure DOM is updated before resetting scroll
    requestAnimationFrame(() => {
      // Reset scroll on drawer-items-component (scroll-hint.drawer__items)
      if (drawerItemsComponent instanceof HTMLElement) {
        drawerItemsComponent.scrollTop = 0;
      }

      // Reset scroll on scroll-hint.drawer__items
      const scrollItems = drawerItemsComponent?.querySelector('scroll-hint.drawer__items');
      if (scrollItems instanceof HTMLElement) {
        scrollItems.scrollTop = 0;
      }

      // Reset scroll on .drawer__content
      const drawer = quickView.closest('drawer-component');
      const drawerContent = drawer?.querySelector('.drawer__content');
      if (drawerContent instanceof HTMLElement) {
        drawerContent.scrollTop = 0;
      }

      // Reset scroll on dialog element itself
      const dialog = drawer?.querySelector('dialog');
      if (dialog instanceof HTMLElement) {
        dialog.scrollTop = 0;
      }

      // Also reset on any elements with scroll-hint class
      const scrollHintElements = drawer?.querySelectorAll('scroll-hint');
      scrollHintElements?.forEach(element => {
        if (element instanceof HTMLElement) {
          element.scrollTop = 0;
        }
      });
    });
  }

  /**
   * @param {HTMLElement} quickView
   */
  #hideSizeChart(quickView) {
    const sizeChartContainer = quickView.querySelector('[data-size-chart-container]');
    const mainContent = quickView.querySelector('[data-quick-view-content-main]');
    const drawerItemsComponent = quickView.closest('drawer-items-component');
    const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');

    if (!sizeChartContainer || !mainContent) {
      console.error('Quick View: Missing required containers');
      return;
    }

    if (sizeChartContainer instanceof HTMLElement) {
      sizeChartContainer.setAttribute('hidden', '');
      sizeChartContainer.style.display = 'none';
    }
    if (mainContent instanceof HTMLElement) mainContent.style.display = 'block';
    if (footerSlot instanceof HTMLElement) footerSlot.style.display = 'block';
  }

  /**
   * Setup listener for drawer close events to reset size chart state
   */
  #setupDrawerCloseListener() {
    const quickView = this.#findQuickViewContainer(this);
    if (!quickView) return;

    // Create handler that resets size chart state when drawer closes
    this.#drawerCloseHandler = () => {
      // Reset size chart state - ensure main content is visible and size chart is hidden
      const sizeChartContainer = quickView.querySelector('[data-size-chart-container]');
      const mainContent = quickView.querySelector('[data-quick-view-content-main]');
      const drawerItemsComponent = quickView.closest('drawer-items-component');
      const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');

      if (sizeChartContainer instanceof HTMLElement) {
        sizeChartContainer.setAttribute('hidden', '');
        sizeChartContainer.style.display = 'none';
      }

      if (mainContent instanceof HTMLElement) {
        mainContent.style.display = 'block';
      }

      if (footerSlot instanceof HTMLElement) {
        footerSlot.style.display = 'block';
      }
    };

    // Listen for native dialog close event (most reliable)
    const drawer = this.closest('drawer-component');
    if (drawer) {
      const dialog = drawer.querySelector('dialog');
      if (dialog) {
        // Use native dialog 'close' event
        dialog.addEventListener('close', this.#drawerCloseHandler);
      }
      
      // Also listen for DialogCloseEvent if it's dispatched
      drawer.addEventListener('dialog:close', this.#drawerCloseHandler);
    }
  }
}

/**
 * Remove duplicate forms/buttons
 * @param {HTMLElement} container
 */
function removeDuplicates(container) {
  const selectors = [
    'product-form-component',
    '.quick-view__actions',
    'add-to-cart-component',
    '[data-add-to-cart]'
  ];

  selectors.forEach((selector) => {
    const elements = container.querySelectorAll(selector);
    if (elements.length > 1) {
      for (let i = 1; i < elements.length; i++) {
        const el = elements[i];
        if (el instanceof HTMLElement) {
          const parent = el.closest('add-to-cart-component');
          (parent || el).remove();
        }
      }
    }
  });
}

/* ================== OVERFLOW SWATCH HANDLER ================== */

const OVERFLOW_SELECTOR = '[data-open-quick-view], button.swatches-default__swatch--overflow[data-quick-view-trigger]';

function getDrawer({ productId, productHandle, productUrl }) {
  let drawer = productId && document.querySelector(
    `drawer-component[id="quick-view-${productId}"], drawer-component[data-product-id="${productId}"]`
  );

  if (!drawer && productHandle) {
    drawer = document.querySelector(`drawer-component[data-product-handle="${productHandle}"]`);
  }

  if (!drawer && productId) {
    drawer = createDrawer(productId, productHandle, productUrl);
  }

  return drawer;
}

function createDrawer(productId, productHandle, productUrl) {
  const drawer = document.createElement('drawer-component');
  drawer.id = `quick-view-${productId}`;
  drawer.dataset.productId = productId;
  if (productHandle) drawer.dataset.productHandle = productHandle;
  if (productUrl) drawer.dataset.productUrl = productUrl;

  const dialog = document.createElement('dialog');
  dialog.className = 'quick-view-drawer__dialog';
  dialog.innerHTML = `<div class="quick-view-drawer__inner"><div class="quick-view-drawer__content" data-drawer-content></div></div>`;

  drawer.appendChild(dialog);
  document.body.appendChild(drawer);
  return drawer;
}

function handleOverflowButtonClick(button) {
  if (button.closest('drawer-button')) return;

  const { productId, productHandle, productUrl, enableClearancePriceColor } = button.dataset;
  const drawer = getDrawer({ productId, productHandle, productUrl });

  // Store enable_clearance_price_color on drawer from trigger button
  if (enableClearancePriceColor !== undefined) {
    drawer.setAttribute('data-enable-clearance-price-color', enableClearancePriceColor);
  }

  if (drawer.open) {
    drawer.open();
  } else if (drawer.showDialog) {
    drawer.showDialog();
  } else {
    drawer.querySelector('dialog')?.showModal();
  }

  scheduleContentLoad(drawer, productHandle, productUrl, null);
}

function attachOverflowButtonListener(button) {
  if (button.dataset.quickViewBound) return;
  button.dataset.quickViewBound = 'true';

  button.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    handleOverflowButtonClick(button);
  }, true);
}

function initOverflowButtons(root = document) {
  root.querySelectorAll(OVERFLOW_SELECTOR).forEach(attachOverflowButtonListener);
}

// Initialize overflow buttons
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initOverflowButtons());
} else {
  initOverflowButtons();
}

// Observer for dynamically added buttons
const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;
      if (node.matches?.(OVERFLOW_SELECTOR)) {
        attachOverflowButtonListener(node);
      } else {
        initOverflowButtons(node);
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });

/* ================== GLOBAL EVENT LISTENERS ================== */

// Drawer button click handler
document.addEventListener('click', (event) => {
  if (!(event.target instanceof HTMLElement)) return;
  const drawerButton = event.target.closest('drawer-button');
  if (!(drawerButton instanceof HTMLElement) || !drawerButton.classList.contains('quick-view-trigger')) return;
  if (event.target.closest('button[type="submit"], form, product-form-component')) return;

  const drawer = drawerButton.closest('drawer-component');
  if (!(drawer instanceof HTMLElement)) return;

  // Read enable_clearance_price_color from drawer-button trigger
  const { enableClearancePriceColor } = drawerButton.dataset;
  if (enableClearancePriceColor !== undefined) {
    drawer.setAttribute('data-enable-clearance-price-color', enableClearancePriceColor);
  }

  const { productHandle, productUrl, variantId } = drawer.dataset;
  if (!productHandle && !productUrl) {
    console.warn('Quick View: Product data not found on drawer', drawer.id);
    return;
  }

  scheduleContentLoad(drawer, productHandle || null, productUrl || null, variantId || null);
}, false);

// Dialog open event listener
document.addEventListener('dialog:open', (event) => {
  if (!(event.target instanceof HTMLElement) || event.target.tagName !== 'DIALOG') return;

  const drawer = event.target.closest('drawer-component');
  if (!(drawer instanceof HTMLElement)) return;

  const drawerButton = drawer.querySelector('drawer-button.quick-view-trigger');
  if (!(drawerButton instanceof HTMLElement)) return;

  // Read enable_clearance_price_color from drawer-button trigger
  const { enableClearancePriceColor } = drawerButton.dataset;
  if (enableClearancePriceColor !== undefined) {
    drawer.setAttribute('data-enable-clearance-price-color', enableClearancePriceColor);
  }

  let { productHandle, productUrl, variantId } = drawer.dataset;

  if (!productHandle && !productUrl) {
    let retryCount = 0;
    const maxRetries = 20;

    const retryInterval = setInterval(() => {
      retryCount++;
      productHandle = drawer.dataset.productHandle || null;
      productUrl = drawer.dataset.productUrl || null;
      variantId = drawer.dataset.variantId || null;

      if (productHandle || productUrl) {
        clearInterval(retryInterval);
        scheduleContentLoad(drawer, productHandle, productUrl, variantId);
      } else if (retryCount >= maxRetries) {
        clearInterval(retryInterval);
        console.warn('Quick View: Product data not found after retries', drawer.id);
      }
    }, 50);
    return;
  }

  scheduleContentLoad(drawer, productHandle || null, productUrl || null, variantId || null);
});

/* ================== CONTENT LOADING ================== */

/**
 * @param {HTMLElement} drawer
 * @param {string | null} productHandle
 * @param {string | null} productUrl
 * @param {string | null} variantId
 */
function scheduleContentLoad(drawer, productHandle, productUrl, variantId) {
  const checkDrawerOpen = () => {
    const dialog = drawer.querySelector('dialog');
    if (dialog?.hasAttribute('open')) {
      openQuickViewDrawer(drawer, productHandle, productUrl, variantId);
      return true;
    }
    return false;
  };

  if (!checkDrawerOpen()) {
    let pollCount = 0;
    const maxPolls = 100;

    const interval = setInterval(() => {
      pollCount++;
      if (checkDrawerOpen()) {
        clearInterval(interval);
      } else if (pollCount >= maxPolls) {
        clearInterval(interval);
        console.warn('Quick View: Drawer did not open within timeout');
      }
    }, 10);
  }
}

/**
 * @param {HTMLElement} drawer
 * @param {string | null} productHandle
 * @param {string | null} productUrl
 * @param {string | null} variantId
 */
async function openQuickViewDrawer(drawer, productHandle, productUrl, variantId) {

  let contentContainer = drawer.querySelector('[data-drawer-content]') ||
                        drawer.querySelector('.drawer__content');

  if (!contentContainer) {
    const dialog = drawer.querySelector('dialog');
    contentContainer = dialog?.querySelector('[data-drawer-content]') || dialog?.querySelector('.drawer__content');
  }

  if (!contentContainer) {
    console.error('Quick View: Content container not found');
    return;
  }

  const existingContent = contentContainer.querySelector('.quick-view');
  const productId = existingContent instanceof HTMLElement ? existingContent.dataset.productId : null;
  const isCachedContent = existingContent && productId && productId === drawer.dataset.productId;

  if (isCachedContent) {
    // Content is cached - get current variant ID from form input (most reliable)
    let currentVariantId = variantId;
    
    // Primary: read data-selected-variant kept in sync by #updateVariant.
    // This is the most reliable source because it is updated every time the user
    // selects a new variant inside the quick view drawer.
    if (existingContent instanceof HTMLElement && existingContent.dataset.selectedVariant) {
      currentVariantId = existingContent.dataset.selectedVariant;
    } else {
    // Try to get variant ID from form input first (most reliable and fastest)
    const variantInput = existingContent.querySelector('input[name="id"], [ref="variantId"]');
    if (variantInput instanceof HTMLInputElement && variantInput.value) {
      currentVariantId = variantInput.value;
    } else {
      // Fallback: calculate from selected options (only if form input is missing)
      const variantsScript = existingContent.querySelector('[data-product-variants]');
      if (variantsScript) {
        try {
          const variants = /** @type {any[]} */ (JSON.parse(variantsScript.textContent || '[]'));
          const selectedOptions = /** @type {Record<number, string>} */ ({});
          
          // Collect selected options
          existingContent.querySelectorAll('[data-option-input]:checked').forEach((input) => {
            if (input instanceof HTMLInputElement) {
              const position = parseInt(input.dataset.optionPosition || '0');
              if (position) {
                selectedOptions[position] = input.value;
              }
            }
          });
          
          // Find matching variant (only if we have selected options)
          if (Object.keys(selectedOptions).length > 0) {
            const matchingVariant = variants.find((variant) => {
              for (const position in selectedOptions) {
                const pos = parseInt(position);
                const optionValue = selectedOptions[pos];
                if (pos > variant.options.length || variant.options[pos - 1] !== optionValue) {
                  return false;
                }
              }
              return true;
            });
            
            if (matchingVariant?.id) {
              currentVariantId = matchingVariant.id.toString();
            }
          }
        } catch (e) {
          // If parsing fails, use fallback variantId
        }
      }
    }
    } // closes: else { // fallback when data-selected-variant not present
    
    // Always reload footer when drawer opens, even with cached content
    const isInStock = !existingContent.querySelector('[data-stock-status] .quick-view__stock--out');
    await loadQuickViewFooter(contentContainer, productHandle, productUrl, currentVariantId, isInStock);
    
    // Check if currently selected swatch is OOS and add drawer-notify-msg class
    const dialog = drawer.querySelector('.drawer__dialog') || drawer.querySelector('dialog');
    if (dialog instanceof HTMLElement) {
      if (!isInStock) {
        dialog.classList.add('drawer-notify-msg');
      } else {
        dialog.classList.remove('drawer-notify-msg');
      }
    }
    
    return;
  }

  // Get clearance price color setting from drawer data attribute
  // This respects the collection type context (normal vs social collection)
  const enableClearancePriceColor = drawer.dataset.enableClearancePriceColor !== 'false';

  if (contentContainer instanceof HTMLElement) {
    // Show drawer loader when content starts loading
    const drawerItemsComponent = contentContainer.closest('drawer-items-component');
    const drawerLoader = drawerItemsComponent?.querySelector('[data-drawer-loader]');
    if (drawerLoader instanceof HTMLElement) {
      drawerLoader.classList.remove('hidden');
      drawerLoader.setAttribute('aria-hidden', 'false');
      drawerLoader.setAttribute('aria-busy', 'true');
    }
    
    // Loading state is now handled by drawer loader, no need for text
    try {
      await loadQuickViewContent(contentContainer, productHandle, productUrl, variantId, enableClearancePriceColor);
      
      // Check if currently selected swatch is OOS and add drawer-notify-msg class
      const quickView = contentContainer.querySelector('.quick-view');
      if (quickView instanceof HTMLElement) {
        const isInStock = !quickView.querySelector('[data-stock-status] .quick-view__stock--out');
        const dialog = drawer.querySelector('.drawer__dialog') || drawer.querySelector('dialog');
        if (dialog instanceof HTMLElement) {
          if (!isInStock) {
            dialog.classList.add('drawer-notify-msg');
          } else {
            dialog.classList.remove('drawer-notify-msg');
          }
        }
      }
    } catch (error) {
      console.error('Quick View: Error loading content', error);
      showErrorState(contentContainer);
      // Hide loader on error
      if (drawerLoader instanceof HTMLElement) {
        drawerLoader.classList.add('hidden');
        drawerLoader.setAttribute('aria-hidden', 'true');
        drawerLoader.setAttribute('aria-busy', 'false');
      }
    }
  }
}

/**
 * Apply price color based on clearance setting and compare_at_price
 * NEW LOGIC: Product is clearance ONLY if compare_at_price exists and is greater than current price
 * Tag-based clearance identification has been removed
 * @param {HTMLElement} quickViewContainer
 */
function applyQuickViewPriceColor(quickViewContainer) {
  if (!(quickViewContainer instanceof HTMLElement)) return;
  
  const enableClearanceColor = quickViewContainer.dataset.enableClearancePriceColor !== 'false';
  
  // Get pricing data from the pricing container
  const pricingContainer = quickViewContainer.querySelector('[data-pricing]');
  const currentPriceEl = pricingContainer?.querySelector('[data-current-price]');
  const comparePriceEl = pricingContainer?.querySelector('[data-compare-price]');
  
  // NEW CLEARANCE LOGIC: Check if compare_at_price exists and is greater than current price
  // by checking if compare price element exists and is visible
  const hasComparePrice = comparePriceEl instanceof HTMLElement && 
                          comparePriceEl.textContent?.trim() !== '' &&
                          comparePriceEl.style.display !== 'none';
  
  const priceElements = quickViewContainer.querySelectorAll('[data-current-price]');
  
  priceElements.forEach(priceEl => {
    if (!(priceEl instanceof HTMLElement)) return;
    
    if (enableClearanceColor && hasComparePrice) {
      // If clearance color is enabled AND product has compare_at_price, add the class
      priceEl.classList.add('quick-view__current-price--clearance');
    } else {
      // If clearance color is disabled OR product doesn't have compare_at_price, remove the class
      priceEl.classList.remove('quick-view__current-price--clearance');
    }
  });
}

/**
 * @param {string} baseUrl
 * @param {string} sectionId
 * @param {string | null} variantId
 * @returns {string}
 */
function buildSectionUrl(baseUrl, sectionId, variantId = null) {
  try {
    const urlObj = new URL(baseUrl, window.location.origin);
    urlObj.search = '';
    urlObj.searchParams.set('section_id', sectionId);
    if (variantId) urlObj.searchParams.set('variant', variantId);
    urlObj.searchParams.set('_', Date.now().toString());
    return urlObj.pathname + urlObj.search;
  } catch {
    const pathname = baseUrl.split('?')[0] || baseUrl;
    const cleanPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
    let url = `${cleanPath}?section_id=${sectionId}`;
    if (variantId) url += `&variant=${variantId}`;
    return url + `&_=${Date.now()}`;
  }
}

/**
 * @param {HTMLElement} contentContainer
 * @param {string | null} productHandle
 * @param {string | null} productUrl
 * @param {string | null} variantId
 * @param {boolean} enableClearancePriceColor
 */
async function loadQuickViewContent(contentContainer, productHandle, productUrl, variantId, enableClearancePriceColor = true) {
  let url = productUrl 
    ? buildSectionUrl(productUrl, 'quick-view-content', variantId)
    : productHandle 
      ? buildSectionUrl(`/products/${productHandle}`, 'quick-view-content', variantId)
      : null;

  if (!url) throw new Error('Missing product handle or URL');

  const response = await fetch(url, {
    headers: { 'X-Requested-With': 'XMLHttpRequest' },
    cache: 'no-store'
  });

  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const html = await response.text();
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const section = doc.querySelector('[data-section-id="quick-view-content"]') ||
                  doc.querySelector('section[data-section-id="quick-view-content"]') ||
                  doc.querySelector('.quick-view-section') ||
                  doc.querySelector('section');

  const quickViewContent = section?.querySelector('.quick-view') ||
                           section?.querySelector('[data-quick-view-content]');

  const itemsContainer = contentContainer.querySelector('.drawer__items') || contentContainer;

  // Get enable_clearance_price_color from drawer BEFORE inserting HTML to prevent FOUC
  const drawer = contentContainer.closest('drawer-component');
  const enableClearance = drawer instanceof HTMLElement && drawer.dataset.enableClearancePriceColor !== undefined
    ? drawer.dataset.enableClearancePriceColor !== 'false'
    : enableClearancePriceColor;

  // Apply price color to HTML string BEFORE inserting to DOM to prevent flash
  let htmlToInsert = '';
  if (quickViewContent && quickViewContent instanceof HTMLElement) {
    // Clone the element to manipulate it
    const clonedContent = quickViewContent.cloneNode(true);
    
    if (clonedContent instanceof HTMLElement) {
      // Update data attribute
      clonedContent.setAttribute('data-enable-clearance-price-color', enableClearance.toString());
      
      // NEW CLEARANCE LOGIC: Check if compare_at_price exists (compare price element is present)
      // Tag-based clearance identification has been removed
      const comparePriceEl = clonedContent.querySelector('[data-compare-price]');
      const hasComparePrice = comparePriceEl instanceof HTMLElement && 
                              comparePriceEl.textContent?.trim() !== '' &&
                              comparePriceEl.style.display !== 'none';
      
      // Apply price color classes before inserting
      const priceElements = clonedContent.querySelectorAll('[data-current-price]');
      priceElements.forEach((priceEl) => {
        if (priceEl instanceof HTMLElement) {
          if (enableClearance && hasComparePrice) {
            priceEl.classList.add('quick-view__current-price--clearance');
          } else {
            priceEl.classList.remove('quick-view__current-price--clearance');
          }
        }
      });
      
      htmlToInsert = clonedContent.outerHTML;
    } else {
      htmlToInsert = quickViewContent.outerHTML;
    }
  } else if (section?.innerHTML) {
    // Parse section HTML and apply same logic
    const tempDoc = new DOMParser().parseFromString(section.innerHTML, 'text/html');
    const tempQuickView = tempDoc.querySelector('.quick-view') || tempDoc.querySelector('[data-quick-view-content]');
    
    if (tempQuickView && tempQuickView instanceof HTMLElement) {
      tempQuickView.setAttribute('data-enable-clearance-price-color', enableClearance.toString());
      
      // NEW CLEARANCE LOGIC: Check if compare_at_price exists (compare price element is present)
      // Tag-based clearance identification has been removed
      const tempComparePriceEl = tempQuickView.querySelector('[data-compare-price]');
      const hasComparePrice = tempComparePriceEl instanceof HTMLElement && 
                              tempComparePriceEl.textContent?.trim() !== '' &&
                              tempComparePriceEl.style.display !== 'none';
      
      const priceElements = tempQuickView.querySelectorAll('[data-current-price]');
      priceElements.forEach((priceEl) => {
        if (priceEl instanceof HTMLElement) {
          if (enableClearance && hasComparePrice) {
            priceEl.classList.add('quick-view__current-price--clearance');
          } else {
            priceEl.classList.remove('quick-view__current-price--clearance');
          }
        }
      });
      
      htmlToInsert = tempQuickView.outerHTML;
    } else {
      htmlToInsert = section.innerHTML;
    }
  } else {
    throw new Error('Quick view content not found in response');
  }

  // Insert the already-corrected HTML (no flash!)
  itemsContainer.innerHTML = htmlToInsert;

  // Footer loading will handle showing/hiding the drawer loader
  await loadQuickViewFooter(contentContainer, productHandle, productUrl, variantId, true);

  const quickViewContainer = itemsContainer.querySelector('[data-quick-view-content]') ||
                             itemsContainer.querySelector('.quick-view') ||
                             itemsContainer;

  if (quickViewContainer instanceof HTMLElement) {
    // Ensure data attribute is set (already done above when cloning/processing)
    quickViewContainer.setAttribute('data-enable-clearance-price-color', enableClearance.toString());

    // Reset size chart state when new content loads - ensure main content is visible
    const sizeChartContainer = quickViewContainer.querySelector('[data-size-chart-container]');
    const mainContent = quickViewContainer.querySelector('[data-quick-view-content-main]');
    const drawerItemsComponent = contentContainer.closest('drawer-items-component');
    const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');

    if (sizeChartContainer instanceof HTMLElement) {
      sizeChartContainer.setAttribute('hidden', '');
      sizeChartContainer.style.display = 'none';
    }

    if (mainContent instanceof HTMLElement) {
      mainContent.style.display = 'block';
    }

    if (footerSlot instanceof HTMLElement) {
      footerSlot.style.display = 'block';
    }

    removeDuplicates(quickViewContainer);

    if (!(quickViewContainer instanceof QuickViewComponent)) {
      const quickViewComponent = new QuickViewComponent();
      quickViewComponent.innerHTML = quickViewContainer.innerHTML;
      quickViewComponent.className = quickViewContainer.className;
      for (const attr of quickViewContainer.attributes) {
        quickViewComponent.setAttribute(attr.name, attr.value);
      }
      quickViewContainer.replaceWith(quickViewComponent);
    }
  }
}

/**
 * @param {HTMLElement} contentContainer
 * @param {string | null} productHandle
 * @param {string | null} productUrl
 * @param {string | null} variantId
 */
async function loadQuickViewFooter(contentContainer, productHandle, productUrl, variantId, isInStock) {
  // Get drawer items component and loader
  const drawerItemsComponent = contentContainer?.closest?.('drawer-items-component') || 
                                contentContainer;
  const drawerLoader = drawerItemsComponent?.querySelector('[data-drawer-loader]');
  
  // Show drawer loader when footer loading starts
  if (drawerLoader instanceof HTMLElement) {
    drawerLoader.classList.remove('hidden');
    drawerLoader.setAttribute('aria-hidden', 'false');
    drawerLoader.setAttribute('aria-busy', 'true');
  }

  const url = productUrl 
    ? buildSectionUrl(productUrl, 'quick-view-footer', variantId)
    : productHandle 
      ? buildSectionUrl(`/products/${productHandle}`, 'quick-view-footer', variantId)
      : null;

  if (!url) {
    console.warn('Quick View Footer: Missing product handle or URL');
    // Hide loader if URL is missing
    if (drawerLoader instanceof HTMLElement) {
      drawerLoader.classList.add('hidden');
      drawerLoader.setAttribute('aria-hidden', 'true');
      drawerLoader.setAttribute('aria-busy', 'false');
    }
    return;
  }

  // Store subscription state before footer reload
  const footerSlot = drawerItemsComponent?.querySelector('[data-drawer-footer]');
  const quickView = contentContainer.querySelector('[data-quick-view-content]') || 
                    contentContainer.querySelector('.quick-view') || 
                    contentContainer;
  const notifyMeForm = quickView?.querySelector('quick-view-notify-me-form');
  const wasSubscribed = notifyMeForm?.dataset.subscribed === 'true';
  const subscribedSku = notifyMeForm?.querySelector('[data-notify-me-sku]')?.value;

  // Store state in quick-view component for restoration after footer loads
  const quickViewComponent = contentContainer.querySelector('quick-view-component');
  if (quickViewComponent instanceof HTMLElement && wasSubscribed && subscribedSku) {
    quickViewComponent.dataset.pendingNotifyMeRestore = 'true';
    quickViewComponent.dataset.pendingNotifyMeSku = subscribedSku;
  }

  try {
    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store'
    });

    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const footerSection = doc.querySelector('[data-section-id="quick-view-footer"]') ||
                          doc.querySelector('[data-quick-view-footer]') ||
                          doc.querySelector('.quick-view-footer') ||
                          doc.querySelector('section');

    const footerContent = footerSection?.querySelector('.quick-view-footer') ||
                          footerSection?.querySelector('[data-quick-view-footer]') ||
                          footerSection;

    if (!footerContent || !footerSlot) return;

    footerSlot.innerHTML = footerContent instanceof HTMLElement ? footerContent.outerHTML : (footerSection?.innerHTML || '');

    // Sync quantity to ALL footer quantity inputs (add-to-cart and shop pay forms)
    const mainQtyInput = contentContainer.querySelector('quantity-selector-component input[type="number"]');
    if (mainQtyInput instanceof HTMLInputElement) {
      const quantityValue = mainQtyInput.value || '1';
      footerSlot.querySelectorAll('[data-quantity-input]').forEach((input) => {
        if (input instanceof HTMLInputElement) {
          input.value = quantityValue;
        }
      });
    }

    // Trigger notification status check after footer loads
    // Dispatch custom event to trigger notify me initialization
    // The loader will be hidden after notification status check completes (see #handleFooterLoaded)
    requestAnimationFrame(() => {
      const quickViewComponent = contentContainer.querySelector('quick-view-component');
      if (quickViewComponent instanceof QuickViewComponent) {
        // Store loader reference in component for later hiding
        if (drawerLoader instanceof HTMLElement) {
          quickViewComponent.dataset.drawerLoaderReady = 'true';
        }
        
        // Dispatch event to trigger re-initialization
        const initEvent = new CustomEvent('quick-view-footer-loaded', {
          bubbles: true,
          detail: { wasSubscribed, subscribedSku, drawerLoader }
        });
        quickViewComponent.dispatchEvent(initEvent);
      } else {
        // If no quick view component, hide loader immediately
        if (drawerLoader instanceof HTMLElement) {
          drawerLoader.classList.add('hidden');
          drawerLoader.setAttribute('aria-hidden', 'true');
          drawerLoader.setAttribute('aria-busy', 'false');
        }
      }
    });
  } catch (error) {
    console.error('Quick View Footer: Error loading content:', error);
    // Hide loader on error
    if (drawerLoader instanceof HTMLElement) {
      drawerLoader.classList.add('hidden');
      drawerLoader.setAttribute('aria-hidden', 'true');
      drawerLoader.setAttribute('aria-busy', 'false');
    }
  }
}



/**
 * @param {HTMLElement} container
 */
function showErrorState(container) {
  const itemsContainer = container.querySelector('.drawer__items') || container;
  itemsContainer.innerHTML = '<div class="quick-view__error" style="padding: 40px 20px; text-align: center; color: #BC0514;">Error loading product. Please try again.</div>';
}

if (!customElements.get('quick-view-component')) {
  customElements.define('quick-view-component', QuickViewComponent);
}